import React, { useState, useEffect, useRef } from 'react';
import config from '../../config';
import { requireUserId } from './userSession';
import { Backdrop } from '@mui/material';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import UploadIcon from '@mui/icons-material/CloudUpload';

import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TablePagination,
  TextField,
  Box,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Grid,
  Snackbar,
  Alert,
  MenuItem,
  Tooltip,
  Divider,
  Chip,
  Drawer,
  Avatar,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Add,
  Remove,
  CheckCircle,
  Edit,
  Cancel,
  PaymentOutlined,
  CloseOutlined,
  ReceiptLongOutlined,
  LocalOfferOutlined,
  CardGiftcardOutlined,
  ExpandMore,
  ExpandLess,
  AccountCircle,
  Undo as UndoIcon,
} from '@mui/icons-material';
import { usePermission, Can, P } from '../rbac';
import { laundryApi, spaApi, miniBarApi } from './modulesApi';
import { useRoomNames } from './useRoomNames';
import { isGstApplicable } from './gstSettings';

const TITLE_OPTIONS = ['Mr.', 'Mrs.', 'Miss', 'Dr.', 'Other'];

const PAYMENT_DRAWER_WIDTH = { xs: '100%', sm: 400, md: 400 };
const PAYMENT_DRAWER_Z_INDEX = 1400;

// Reusable sx for <DialogActions> — stacks buttons vertically on phones and
// makes them full-width so they're easier to tap. `column-reverse` keeps the
// primary action (rightmost on desktop) above the cancel button on mobile.
const RESPONSIVE_DIALOG_ACTIONS_SX = {
  px: { xs: 2, sm: 3 },
  py: { xs: 1.5, sm: 1 },
  flexDirection: { xs: 'column-reverse', sm: 'row' },
  alignItems: { xs: 'stretch', sm: 'center' },
  gap: { xs: 1, sm: 0 },
  '& > button': {
    width: { xs: '100%', sm: 'auto' },
    ml: { xs: '0 !important', sm: undefined },
  },
};

// Shared date/time formatters — single source of truth so every dialog,
// invoice, snackbar, and exported field renders the same format.
//   fmtDateTime: "15 Mar 2026, 11:30 AM"
//   fmtDate:     "15 Mar 2026"
// Returns "—" for null / invalid input rather than the literal string
// "Invalid Date" or "1970…".
const fmtDateTime = (value) => {
  if (!value) return '—';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};
const fmtDate = (value) => {
  if (!value) return '—';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

/** Drawer theme tokens — aligned with Manage Bookings page */
const drawerTheme = {
  primary: '#1565c0',
  primaryLight: '#1976d2',
  bg: '#f0f4f8',
  surface: '#fff',
  border: '#e3eaf3',
  divider: '#e8edf3',
  textMuted: 'rgba(0,0,0,0.55)',
  success: '#2e7d32',
  danger: '#c62828',
};

function DrawerSection({ title, children, sx }) {
  return (
    <Box sx={{ mb: 2, ...sx }}>
      <Typography
        sx={{
          fontSize: 10,
          fontWeight: 700,
          color: drawerTheme.primary,
          textTransform: 'uppercase',
          letterSpacing: 0.8,
          mb: 0.75,
          px: 0.25,
        }}>
        {title}
      </Typography>
      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${drawerTheme.border}`,
          borderRadius: 2,
          bgcolor: drawerTheme.surface,
          overflow: 'hidden',
        }}>
        {children}
      </Paper>
    </Box>
  );
}

function DrawerRow({ label, value, highlight, isLast, badge }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
        px: 1.75,
        py: 1.1,
        minHeight: 40,
        bgcolor: highlight ? '#f0f7ff' : 'transparent',
        borderBottom: isLast ? 'none' : `1px solid ${drawerTheme.divider}`,
      }}>
      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: 12.5,
            color: highlight ? drawerTheme.primary : drawerTheme.textMuted,
            fontWeight: highlight ? 600 : 400,
            lineHeight: 1.3,
          }}>
          {label}
        </Typography>
        {badge}
      </Box>
      <Typography
        sx={{
          fontSize: highlight ? 13.5 : 12.5,
          fontWeight: highlight ? 700 : 600,
          color: highlight ? drawerTheme.primary : 'text.primary',
          whiteSpace: 'nowrap',
          flexShrink: 0,
        }}>
        {value}
      </Typography>
    </Box>
  );
}

// ─── Payment Details Drawer Component ────────────────────────────────────────
function PaymentDetailsDrawer({ open, onClose, booking, currency, roomLabel }) {
  // Raw CR/DR ledger for this booking (debits = charges, credits = payments).
  const [ledger, setLedger] = React.useState([]);
  React.useEffect(() => {
    if (!open || !booking?.bookingId) {
      setLedger([]);
      return;
    }
    const token = sessionStorage.getItem('token');
    fetch(`${config.API_BASE_URL}/bookingledger/${booking.bookingId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((d) => setLedger(Array.isArray(d) ? d : []))
      .catch(() => setLedger([]));
  }, [open, booking?.bookingId]);

  if (!booking) return null;
  const labelRoom = roomLabel || ((v) => v);

  const guestName =
    `${booking.title ? `${booking.title} ` : ''}${booking.firstName || ''} ${booking.lastName || ''}`.trim() ||
    '—';
  const initials = (booking.firstName || 'G').charAt(0).toUpperCase();
  const pending = parseFloat(booking.pendingAmount || 0);

  const basePrice = booking.baseprice || booking.price || 0;
  const totalPrice = booking.totelPrice || 0;
  const withGst = booking.finalpriceWithGST || 0;

  // Coerce every branch to a real boolean. Bare `||` chains can land on a
  // numeric 0 / empty string, and JSX renders the literal `0` to the DOM
  // (only `false`/`null`/`undefined` are skipped) — which was leaking stray
  // "0"s under the Discounts Applied section when discount values were 0.
  const hasDiscounts = Boolean(
    booking.discountType ||
      Number(booking.discountValue) > 0 ||
      booking.extraDiscountType ||
      Number(booking.extraDiscountValue) > 0,
  );
  const hasExtras = Boolean(
    Number(booking.addonWithGstTotal) > 0 ||
      Number(booking.covercharge) > 0 ||
      booking.coupen ||
      Number(booking.couponAmount) > 0,
  );

  const fmt = (n) => `${currency}${n ?? 0}`;
  const fmtCredit = (n) => (n > 0 ? `-${currency}${n}` : `${currency}0`);

  return (
    <Drawer
      anchor='right'
      open={open}
      onClose={onClose}
      variant='temporary'
      transitionDuration={280}
      disableScrollLock={false}
      sx={{ zIndex: PAYMENT_DRAWER_Z_INDEX }}
      ModalProps={{
        keepMounted: false,
        sx: { zIndex: PAYMENT_DRAWER_Z_INDEX },
      }}
      BackdropProps={{
        sx: {
          bgcolor: 'rgba(0, 0, 0, 0.35)',
          backdropFilter: 'blur(2px)',
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            bgcolor: 'rgba(0, 0, 0, 0.35)',
            backdropFilter: 'blur(2px)',
          },
        },
      }}
      PaperProps={{
        sx: {
          // Let MUI's temporary Drawer own the positioning (same as the working
          // Order Details drawers). A hand-rolled `position: fixed` is positioned
          // relative to any ancestor that has a CSS transform (common in mobile
          // layouts), which pushed this panel off-screen on phones — that's why
          // the Payment Summary "didn't show" on mobile. Native positioning is
          // robust across all viewports.
          height: '100%',
          width: PAYMENT_DRAWER_WIDTH,
          maxWidth: '100vw',
          bgcolor: drawerTheme.bg,
          borderLeft: `1px solid ${drawerTheme.border}`,
          borderTopLeftRadius: '20px',
          borderBottomLeftRadius: '20px',
          boxShadow: '-8px 0 30px rgba(0, 0, 0, 0.15)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        },
      }}>
      {/* Sticky header */}
      <Box
        sx={{
          flexShrink: 0,
          bgcolor: drawerTheme.primary,
          color: '#fff',
          px: 2,
          pt: 1.75,
          pb: 1.5,
          borderTopLeftRadius: '20px',
          borderBottom: `1px solid ${drawerTheme.primaryLight}`,
        }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 1,
            mb: 1.25,
          }}>
          <Box minWidth={0}>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: 16,
                color: '#fff',
                lineHeight: 1.25,
                letterSpacing: 0.2,
              }}>
              Payment Summary
            </Typography>
            <Typography
              sx={{
                fontSize: 11.5,
                color: 'rgba(255,255,255,0.88)',
                mt: 0.35,
                lineHeight: 1.3,
              }}>
              Booking #{booking.bookingId} · Room {booking.roomNumbers ? labelRoom(booking.roomNumbers) : '—'}
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            size='small'
            aria-label='Close payment summary'
            sx={{
              color: '#fff',
              width: 30,
              height: 30,
              mt: -0.25,
              bgcolor: 'rgba(255,255,255,0.14)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.24)' },
            }}>
            <CloseOutlined sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <Avatar
            sx={{
              width: 34,
              height: 34,
              bgcolor: 'rgba(255,255,255,0.2)',
              color: '#fff',
              fontSize: 14,
              fontWeight: 700,
              border: '1.5px solid rgba(255,255,255,0.35)',
            }}>
            {initials}
          </Avatar>
          <Typography
            sx={{
              fontSize: 13.5,
              fontWeight: 600,
              color: '#fff',
              lineHeight: 1.25,
            }}
            noWrap>
            {guestName}
          </Typography>
        </Box>
      </Box>

      {/* Scrollable body */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          px: 2,
          py: 1.75,
          WebkitOverflowScrolling: 'touch',
        }}>
        {/* Section 1 — Booking Overview */}
        <DrawerSection title='Booking Overview'>
          <DrawerRow label='Guest Name' value={guestName} />
          <DrawerRow label='Room No.' value={booking.roomNumbers ? labelRoom(booking.roomNumbers) : '—'} />
          <DrawerRow
            label='Booking ID'
            value={`#${booking.bookingId}`}
            isLast
          />
        </DrawerSection>

        {/* Section 2 — Price Breakdown */}
        <DrawerSection title='Price Breakdown'>
          <DrawerRow label='Base Price' value={fmt(basePrice)} />
          <DrawerRow label='Total Price' value={fmt(totalPrice)} />
          <DrawerRow
            label={isGstApplicable() ? 'With GST' : 'Total'}
            value={fmt(withGst)}
            highlight
            isLast
            badge={
              isGstApplicable() ? (
              <Chip
                label='GST'
                size='small'
                sx={{
                  height: 18,
                  fontSize: 9,
                  fontWeight: 700,
                  bgcolor: '#e3f2fd',
                  color: drawerTheme.primary,
                  '& .MuiChip-label': { px: 0.75 },
                }}
              />
              ) : null
            }
          />
        </DrawerSection>

        {/* Section 3 — Discounts Applied */}
        {hasDiscounts && (
          <DrawerSection title='Discounts Applied'>
            {Boolean(
              booking.discountType || Number(booking.discountValue) > 0,
            ) && (
              <>
                <DrawerRow
                  label='Discount Type'
                  value={booking.discountType || '—'}
                />
                <DrawerRow
                  label='Discount Value'
                  value={fmt(booking.discountValue || 0)}
                  isLast={
                    !booking.extraDiscountType &&
                    !(Number(booking.extraDiscountValue) > 0)
                  }
                />
              </>
            )}
            {Boolean(
              booking.extraDiscountType ||
                Number(booking.extraDiscountValue) > 0,
            ) && (
              <>
                <DrawerRow
                  label='Extra Discount Type'
                  value={booking.extraDiscountType || '—'}
                />
                <DrawerRow
                  label='Extra Discount Value'
                  value={fmt(booking.extraDiscountValue || 0)}
                  isLast
                />
              </>
            )}
          </DrawerSection>
        )}

        {/* Section 4 — Additional Charges */}
        {hasExtras && (
          <DrawerSection title='Additional Charges'>
            {booking.addonWithGstTotal > 0 && (
              <DrawerRow
                label='Add On Charge'
                value={fmt(booking.addonWithGstTotal)}
              />
            )}
            {booking.covercharge > 0 && (
              <DrawerRow
                label='Cover Charge'
                value={fmt(booking.covercharge)}
              />
            )}
            {(booking.coupen || booking.couponAmount > 0) && (
              <>
                <DrawerRow label='Voucher' value={booking.coupen || '—'} />
                <DrawerRow
                  label='Voucher Amount'
                  value={fmtCredit(booking.couponAmount || 0)}
                  isLast
                />
              </>
            )}
          </DrawerSection>
        )}

        {/* Ledger — every Debit (charge) and Credit (payment) line */}
        {ledger.length > 0 && (
          <DrawerSection title='Ledger (Debit / Credit)'>
            {ledger.map((t, i) => {
              const isCr = (t.type || '').toUpperCase() === 'CR';
              return (
                <Box
                  key={i}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    py: 0.5,
                    borderBottom:
                      i < ledger.length - 1 ? '1px dashed #eee' : 'none',
                    opacity: t.isActive === false ? 0.45 : 1,
                  }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 600 }} noWrap>
                      {t.mode || '—'}
                    </Typography>
                    <Typography
                      sx={{ fontSize: 9, color: 'text.secondary' }}
                      noWrap>
                      {t.date
                        ? new Date(t.date).toLocaleString('en-IN')
                        : ''}
                    </Typography>
                  </Box>
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Chip
                      label={isCr ? 'CR' : 'DR'}
                      size='small'
                      color={isCr ? 'success' : 'error'}
                      sx={{ height: 16, fontSize: 9 }}
                    />
                    <Typography
                      sx={{
                        fontSize: 12,
                        fontWeight: 700,
                        color: isCr
                          ? drawerTheme.success
                          : drawerTheme.danger,
                      }}>
                      {isCr ? '+' : '-'}
                      {currency}
                      {t.amount}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </DrawerSection>
        )}

        {/* Section 5 — Final Amount Summary */}
        <Box
          sx={{
            mt: 0.5,
            p: 1.75,
            borderRadius: 2,
            bgcolor: pending > 0 ? '#fff5f5' : '#f0fff4',
            border: `1.5px solid ${pending > 0 ? '#ffcdd2' : '#c8e6c9'}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
          }}>
          <Box>
            <Typography
              sx={{
                fontSize: 10,
                fontWeight: 700,
                color: pending > 0 ? drawerTheme.danger : drawerTheme.success,
                textTransform: 'uppercase',
                letterSpacing: 0.6,
              }}>
              Total Payable
            </Typography>
            <Typography
              sx={{ fontSize: 11, color: drawerTheme.textMuted, mt: 0.25 }}>
              {pending > 0 ? 'Pending balance' : 'Fully settled'}
            </Typography>
          </Box>
          <Typography
            sx={{
              fontSize: 22,
              fontWeight: 800,
              color: pending > 0 ? drawerTheme.danger : drawerTheme.success,
              lineHeight: 1,
            }}>
            {currency}
            {Math.round(pending)}
          </Typography>
        </Box>
      </Box>
    </Drawer>
  );
}

// ─── Status Badge ─────────────────────────────────────────────────────────────
function BookingStatusBadge({ booking }) {
  const isCheckedIn = [true, 'true', 1].includes(booking.ischeckin);
  const isInvoiced = [true, 'true', 1].includes(booking.isinvoice);
  const isCancelled = !!booking.isCancel;

  if (isCancelled)
    return (
      <Chip
        label='Cancelled'
        size='small'
        sx={{
          bgcolor: '#ffebee',
          color: '#c62828',
          fontWeight: 'bold',
          fontSize: 11,
          height: 22,
        }}
      />
    );
  if (isInvoiced)
    return (
      <Chip
        label='Checked Out'
        size='small'
        sx={{
          bgcolor: '#e8f5e9',
          color: '#2e7d32',
          fontWeight: 'bold',
          fontSize: 11,
          height: 22,
        }}
      />
    );
  if (isCheckedIn)
    return (
      <Chip
        label='Checked In'
        size='small'
        sx={{
          bgcolor: '#e3f2fd',
          color: '#1565c0',
          fontWeight: 'bold',
          fontSize: 11,
          height: 22,
        }}
      />
    );
  return (
    <Chip
      label='Confirmed'
      size='small'
      sx={{
        bgcolor: '#fff8e1',
        color: '#e65100',
        fontWeight: 'bold',
        fontSize: 11,
        height: 22,
      }}
    />
  );
}

export default function BookingReport() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { hasPermission, hasAnyPermission } = usePermission();
  const { roomLabel } = useRoomNames();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [searchText, setSearchText] = useState('');

  // ─── Payment Drawer State ───────────────────────────────────────────────────
  const [paymentDrawerOpen, setPaymentDrawerOpen] = useState(false);
  const [drawerBooking, setDrawerBooking] = useState(null);

  const handleOpenPaymentDrawer = (booking) => {
    setDrawerBooking(booking);
    setPaymentDrawerOpen(true);
  };

  // Add-on dialog
  const [addonDialogOpen, setAddonDialogOpen] = useState(false);
  const [currentBooking, setCurrentBooking] = useState(null);
  const [addonConfirmOpen, setAddonConfirmOpen] = useState(false);
  const [addonInputOpen, setAddonInputOpen] = useState(false);
  const [bookingForInvoice, setBookingForInvoice] = useState(null);
  const [addons, setAddons] = useState([{ name: '', price: '', qty: '1' }]);
  const [discountModalOpen, setDiscountModalOpen] = useState(false);
  const [currentDiscountBooking, setCurrentDiscountBooking] = useState(null);
  const [discountAmount, setDiscountAmount] = useState('');
  const [discountRemark, setDiscountRemark] = useState('');
  const [photos, setPhotos] = useState([]);
  // Aadhaar front/back files staged in the Update dialog. Sent to
  // /Home/UploadBookingAadhar on Save. These are separate from `photos`
  // because the legacy free-form photo array never had a persistence path.
  const [aadharFront, setAadharFront] = useState(null);
  const [aadharBack, setAadharBack] = useState(null);
  // Loading state for the GetUserIdData OCR call that fires when the user
  // has chosen BOTH sides. Kept separate from `actionLoading` so the dialog
  // can show "Scanning Aadhaar…" instead of the generic action backdrop.
  const [ocrLoading, setOcrLoading] = useState(false);
  // Headcount + co-guest editor for the Update dialog. Initialised from
  // the booking's saved adult/child count and a follow-up fetch to
  // /Home/GetBookingOtherGuests so existing co-guests show up.
  const [updateAdultCount, setUpdateAdultCount] = useState(1);
  const [updateChildCount, setUpdateChildCount] = useState(0);
  const [updateOtherGuests, setUpdateOtherGuests] = useState([]);
  const [roomFilter, setRoomFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('All');
  const [updateDiscountType, setUpdateDiscountType] = useState('Rupee');
  const [updateDiscountValue, setUpdateDiscountValue] = useState('');
  const [manageDialogOpen, setManageDialogOpen] = useState(false);
  const [selectedManageBooking, setSelectedManageBooking] = useState(null);
  const [invoicePreviewOpen, setInvoicePreviewOpen] = useState(false);
  // Addons fetched fresh when the Preview Invoice dialog opens — used to
  // render itemised add-on rows in the invoice table.
  const [previewAddons, setPreviewAddons] = useState([]);

  const [confirmationSlipOpen, setConfirmationSlipOpen] = useState(false);
  const [confirmationBooking, setConfirmationBooking] = useState(null);

  // Services breakdown dialog — shows every Spa booking, Laundry order,
  // Mini-Bar consumption, and Restaurant order linked to the current room
  // booking with line totals.
  const [servicesOpen, setServicesOpen] = useState(false);
  // Which breakdown the (reused) dialog is showing: 'services' = Spa / Laundry
  // / Mini-Bar, 'restaurant' = restaurant orders. Each has its own button.
  const [servicesView, setServicesView] = useState('services');
  const [servicesLoading, setServicesLoading] = useState(false);
  const [servicesData, setServicesData] = useState({
    booking: null,
    spa: [],
    laundry: [],
    minibar: [],
    restaurant: [],
  });
  // Per-order item cache for restaurant — fetched lazily when the user
  // clicks the View Items button on a row.
  // Shape: { [orderId]: { loading: bool, items: OrderDetailSummaryDto[] } }
  const [restroItems, setRestroItems] = useState({});

  const fmtDateSafe = (v) => {
    if (!v) return '-';
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? '-' : d.toLocaleString('en-IN');
  };

  const toggleRestroItems = async (orderId) => {
    const existing = restroItems[orderId];
    if (existing && existing.items) {
      // Already loaded — toggle visibility off by removing the entry.
      const { [orderId]: _, ...rest } = restroItems;
      void _;
      setRestroItems(rest);
      return;
    }
    setRestroItems({ ...restroItems, [orderId]: { loading: true, items: null } });
    try {
      const token = sessionStorage.getItem('token');
      const res = await fetch(
        `${config.API_BASE_URL}/Home/GetOrderDetails/${orderId}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const items = res.ok ? await res.json() : [];
      setRestroItems((prev) => ({ ...prev, [orderId]: { loading: false, items } }));
    } catch {
      setRestroItems((prev) => ({ ...prev, [orderId]: { loading: false, items: [] } }));
    }
  };

  const openServicesView = async (booking, view = 'services') => {
    setServicesView(view);
    setServicesOpen(true);
    setServicesLoading(true);
    setServicesData({
      booking, spa: [], laundry: [], minibar: [], restaurant: [],
    });
    try {
      const hotelId = booking.hotelId || company?.hotelId || company?.id;
      const token = sessionStorage.getItem('token');
      // Spa + Laundry list endpoints don't accept bookingId — fetch a page
      // wide enough for this hotel and filter client-side. Mini-Bar accepts
      // bookingId directly. Restaurant orders come from the legacy /Home
      // endpoint and are also filtered client-side.
      const [
        { data: spaData },
        { data: laundryData },
        { data: mbData },
        restroRes,
      ] = await Promise.all([
        spaApi.listBookings(hotelId, { pageSize: 500 }),
        laundryApi.listOrders(hotelId, { pageSize: 500 }),
        miniBarApi.listConsumptions(hotelId, { bookingId: booking.bookingId }),
        fetch(`${config.API_BASE_URL}/Home/Getallaorders/${company?.id}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      // Match on booking id loosely — spa/laundry summaries sometimes carry the
      // id as a string, so a strict === against the numeric booking id silently
      // dropped every row (services looked empty even though orders existed).
      const sameBooking = (id) => Number(id) === Number(booking.bookingId);
      const spaSummaries = (spaData?.items || []).filter((s) =>
        sameBooking(s.bookingId),
      );
      const laundrySummaries = (laundryData?.items || []).filter((o) =>
        sameBooking(o.bookingId),
      );
      const minibar = Array.isArray(mbData) ? mbData : (mbData?.items || []);
      let restaurant = [];
      if (restroRes.ok) {
        const all = await restroRes.json();
        restaurant = (Array.isArray(all) ? all : []).filter((o) =>
          sameBooking(o.bookingId),
        );
      }

      // Pull full details (with line items) for each spa booking and
      // laundry order so the dialog can show the individual services /
      // items, not just the rolled-up totals.
      const [spaFull, laundryFull] = await Promise.all([
        Promise.all(
          spaSummaries.map((s) =>
            spaApi.getBooking(s.spaBookingId)
              .then((r) => r.data)
              .catch(() => s),
          ),
        ),
        Promise.all(
          laundrySummaries.map((o) =>
            laundryApi.getOrder(o.laundryOrderId)
              .then((r) => r.data)
              .catch(() => o),
          ),
        ),
      ]);

      setServicesData({
        booking,
        spa: spaFull,
        laundry: laundryFull,
        minibar,
        restaurant,
      });
    } catch (e) {
      setSnackbar({
        open: true,
        message: 'Failed to load services',
        severity: 'error',
      });
    } finally {
      setServicesLoading(false);
    }
  };

  const handleOpenConfirmation = (booking) => {
    setConfirmationBooking(booking);
    setConfirmationSlipOpen(true);
  };

  const handlePrintConfirmation = () => {
    const printContents = document.getElementById(
      'printableConfirmation',
    ).innerHTML;
    const win = window.open('', '', 'width=1200,height=900');
    win.document.write(
      `<html><head><title>Booking Confirmation</title><style>*{box-sizing:border-box;margin:0;padding:0;}body{font-family:Arial,sans-serif;background:#f2f2f2;padding:20px;}#wrapper{max-width:900px;margin:auto;background:#fff;border:2px solid #1976d2;border-radius:8px;padding:0;overflow:hidden;}.header-band{background:#1976d2;color:#fff;padding:24px 32px;display:flex;justify-content:space-between;align-items:center;}.header-band h1{font-size:26px;font-weight:bold;}.header-band .booking-id{font-size:14px;opacity:0.9;margin-top:4px;}.status-badge{background:#43a047;color:#fff;border-radius:20px;padding:6px 18px;font-size:14px;font-weight:bold;}.section{padding:20px 32px;border-bottom:1px solid #e0e0e0;}.section:last-child{border-bottom:none;}.section-title{font-size:15px;font-weight:bold;color:#1976d2;margin-bottom:12px;text-transform:uppercase;letter-spacing:0.5px;}.info-grid{display:grid;grid-template-columns:1fr 1fr;gap:10px 24px;}.info-item label{font-size:11px;color:#888;text-transform:uppercase;display:block;margin-bottom:2px;}.info-item span{font-size:14px;color:#222;font-weight:500;}table{width:100%;border-collapse:collapse;}table th{background:#e8f0fe;font-size:12px;color:#1976d2;text-transform:uppercase;padding:10px 12px;text-align:left;}table td{padding:10px 12px;font-size:13px;border-bottom:1px solid #f0f0f0;}@media print{body{background:#fff;}}</style></head><body><div id="wrapper">${printContents}</div></body></html>`,
    );
    win.document.close();
    setTimeout(() => {
      win.focus();
      win.print();
    }, 500);
  };

  const handleDownloadConfirmationPDF = () => {
    const printContents = document.getElementById(
      'printableConfirmation',
    ).innerHTML;
    const win = window.open('', '', 'width=1200,height=900');
    win.document.write(
      `<html><head><title>Booking Confirmation - ${confirmationBooking?.bookingId}</title><style>*{box-sizing:border-box;margin:0;padding:0;}body{font-family:Arial,sans-serif;background:#fff;padding:20px;}#wrapper{max-width:900px;margin:auto;background:#fff;border:2px solid #1976d2;border-radius:8px;overflow:hidden;}table{width:100%;border-collapse:collapse;}table th{background:#e8f0fe;font-size:12px;color:#1976d2;text-transform:uppercase;padding:10px 12px;text-align:left;}table td{padding:10px 12px;font-size:13px;border-bottom:1px solid #f0f0f0;}@media print{body{background:#fff;}}</style></head><body><div id="wrapper">${printContents}</div><script>window.onload=function(){window.print();};<\/script></body></html>`,
    );
    win.document.close();
  };

  const ConfirmationSlipContent = ({ b }) => {
    if (!b) return null;
    const guestTitle = b.title ? `${b.title} ` : '';
    const fullName =
      `${guestTitle}${b.firstName || ''} ${b.lastName || ''}`.trim();
    // Use shared fmtDateTime so confirmation slip matches every other dialog.
    const checkInStr = fmtDateTime(b.checkIn);
    const checkOutStr = fmtDateTime(b.checkOut);
    const pending = parseFloat(b.pendingAmount || 0);
    const isCheckedIn = [true, 'true', 1].includes(b.ischeckin);
    const isInvoiced = [true, 'true', 1].includes(b.isinvoice);
    const isCancelled = !!b.isCancel;
    const statusLabel = isCancelled
      ? 'CANCELLED'
      : isInvoiced
        ? 'CHECKED OUT'
        : isCheckedIn
          ? 'CHECKED IN'
          : 'CONFIRMED';
    const statusColor = isCancelled
      ? '#d32f2f'
      : isCheckedIn
        ? '#1976d2'
        : '#43a047';

    const hotelName =
      b.hotelName || company?.name || company?.companyName || 'Hotel';
    const hotelAddressLine = [
      company?.address,
      company?.city,
      company?.location,
      company?.pincode,
    ]
      .filter(Boolean)
      .join(', ');
    const receptionNo =
      company?.ReceptionNo || company?.receptionNo || company?.reception || '';

    // Nights = ceil((checkOut - checkIn) / 1 day). Falls back to "—" when
    // either date is missing/invalid so the slip never prints "NaN nights".
    const computeNights = () => {
      if (!b.checkIn || !b.checkOut) return '—';
      const inD = new Date(b.checkIn);
      const outD = new Date(b.checkOut);
      if (Number.isNaN(inD.getTime()) || Number.isNaN(outD.getTime()))
        return '—';
      const diff = Math.ceil((outD - inD) / (1000 * 60 * 60 * 24));
      return diff > 0 ? diff : 1;
    };
    const nights = computeNights();

    return (
      <div
        id='printableConfirmation'
        style={{
          fontFamily: 'Arial, sans-serif',
          background: '#fff',
          borderRadius: 8,
          overflow: 'hidden',
          border: '2px solid #1976d2',
        }}>
        <div
          style={{
            background: '#1976d2',
            color: '#fff',
            padding: '24px 32px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 16,
          }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 22, fontWeight: 'bold' }}>{hotelName}</div>
            {hotelAddressLine && (
              <div
                style={{
                  fontSize: 12,
                  opacity: 0.9,
                  marginTop: 4,
                  lineHeight: 1.4,
                }}>
                {hotelAddressLine}
              </div>
            )}
            {(company?.mobile || receptionNo) && (
              <div
                style={{
                  fontSize: 12,
                  opacity: 0.9,
                  marginTop: 3,
                  lineHeight: 1.4,
                }}>
                {company?.mobile && <span>Phone: {company.mobile}</span>}
                {receptionNo && (
                  <span style={{ marginLeft: company?.mobile ? 12 : 0 }}>
                    Reception: {receptionNo}
                  </span>
                )}
              </div>
            )}
            {company?.email && (
              <div style={{ fontSize: 12, opacity: 0.85, marginTop: 2 }}>
                Email: {company.email}
              </div>
            )}
            {company?.gstNumber && (
              <div style={{ fontSize: 12, opacity: 0.85, marginTop: 2 }}>
                GSTIN: {company.gstNumber}
              </div>
            )}
            <div
              style={{
                fontSize: 13,
                opacity: 0.95,
                marginTop: 10,
                fontWeight: 600,
              }}>
              Booking Confirmation &middot; #{b.bookingId}
            </div>
          </div>
          <div
            style={{
              background: statusColor,
              color: '#fff',
              borderRadius: 20,
              padding: '8px 20px',
              fontSize: 13,
              fontWeight: 'bold',
              letterSpacing: 1,
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}>
            {statusLabel}
          </div>
        </div>
        <div style={{ display: 'flex', borderBottom: '1px solid #e0e0e0' }}>
          <div
            style={{
              flex: 1,
              padding: '18px 28px',
              background: '#f5f8ff',
              borderRight: '1px solid #dde6f7',
            }}>
            <div
              style={{
                fontSize: 11,
                color: '#1976d2',
                fontWeight: 'bold',
                textTransform: 'uppercase',
                marginBottom: 4,
              }}>
              Check-In
            </div>
            <div style={{ fontSize: 17, fontWeight: 'bold', color: '#222' }}>
              {checkInStr}
            </div>
          </div>
          <div style={{ flex: 1, padding: '18px 28px', background: '#f5f8ff' }}>
            <div
              style={{
                fontSize: 11,
                color: '#1976d2',
                fontWeight: 'bold',
                textTransform: 'uppercase',
                marginBottom: 4,
              }}>
              Check-Out
            </div>
            <div style={{ fontSize: 17, fontWeight: 'bold', color: '#222' }}>
              {checkOutStr}
            </div>
          </div>
        </div>
        <div
          style={{ padding: '20px 32px', borderBottom: '1px solid #e0e0e0' }}>
          <div
            style={{
              fontSize: 13,
              fontWeight: 'bold',
              color: '#1976d2',
              textTransform: 'uppercase',
              marginBottom: 12,
            }}>
            Room Details
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '10px 24px',
            }}>
            {[
              ['Hotel', hotelName],
              ['Room No.', b.roomNumbers ? roomLabel(b.roomNumbers) : '—'],
              ['Room Type', b.roomType || '—'],
              ['Nights', nights],
              ['Check-in Time', b.checkInTime || '—'],
              ['Check-out Time', b.checkOutTime || '—'],
              [
                'Adults',
                b.adultCount !== undefined && b.adultCount !== null
                  ? b.adultCount
                  : '—',
              ],
              [
                'Children',
                b.childCount !== undefined && b.childCount !== null
                  ? b.childCount
                  : '—',
              ],
              ['Booking Source', b.through || '—'],
              ['Reception No.', receptionNo || '—'],
            ].map(([label, val]) => (
              <div key={label}>
                <div
                  style={{
                    fontSize: 10,
                    color: '#888',
                    textTransform: 'uppercase',
                    marginBottom: 2,
                  }}>
                  {label}
                </div>
                <div style={{ fontSize: 14, fontWeight: 500 }}>{val}</div>
              </div>
            ))}
          </div>
        </div>
        <div
          style={{ padding: '20px 32px', borderBottom: '1px solid #e0e0e0' }}>
          <div
            style={{
              fontSize: 13,
              fontWeight: 'bold',
              color: '#1976d2',
              textTransform: 'uppercase',
              marginBottom: 12,
            }}>
            Guest Details
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '10px 24px',
            }}>
            {[
              ['Guest Name', fullName || '—'],
              ['Mobile', b.mobileNumber || '—'],
              ['Email', b.email || '—'],
              [
                'Address',
                b.address ? `${b.address}${b.city ? ', ' + b.city : ''}` : '—',
              ],
              ['ID Type', b.idType || '—'],
              ['ID Number', b.idNumber || '—'],
            ].map(([label, val]) => (
              <div key={label}>
                <div
                  style={{
                    fontSize: 10,
                    color: '#888',
                    textTransform: 'uppercase',
                    marginBottom: 2,
                  }}>
                  {label}
                </div>
                <div style={{ fontSize: 14, fontWeight: 500 }}>{val}</div>
              </div>
            ))}
          </div>
        </div>
        <div
          style={{ padding: '20px 32px', borderBottom: '1px solid #e0e0e0' }}>
          <div
            style={{
              fontSize: 13,
              fontWeight: 'bold',
              color: '#1976d2',
              textTransform: 'uppercase',
              marginBottom: 12,
            }}>
            Payment Summary
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#e8f0fe' }}>
                <th
                  style={{
                    fontSize: 11,
                    color: '#1976d2',
                    textTransform: 'uppercase',
                    padding: '8px 12px',
                    textAlign: 'left',
                  }}>
                  Description
                </th>
                <th
                  style={{
                    fontSize: 11,
                    color: '#1976d2',
                    textTransform: 'uppercase',
                    padding: '8px 12px',
                    textAlign: 'right',
                  }}>
                  Amount
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td
                  style={{
                    padding: '8px 12px',
                    fontSize: 13,
                    borderBottom: '1px solid #f0f0f0',
                  }}>
                  Base Price
                </td>
                <td
                  style={{
                    padding: '8px 12px',
                    fontSize: 13,
                    textAlign: 'right',
                    borderBottom: '1px solid #f0f0f0',
                  }}>
                  {Companycurrency} {b.baseprice || b.price || 0}
                </td>
              </tr>
              <tr>
                <td
                  style={{
                    padding: '8px 12px',
                    fontSize: 13,
                    borderBottom: '1px solid #f0f0f0',
                  }}>
                  Total Price
                </td>
                <td
                  style={{
                    padding: '8px 12px',
                    fontSize: 13,
                    textAlign: 'right',
                    borderBottom: '1px solid #f0f0f0',
                  }}>
                  {/* For a VOUCHER booking the stored totelPrice is the
                      post-voucher value (often 0), which made this row show 0
                      while the voucher line below still subtracts the full
                      amount. Show the PRE-voucher gross so the math reads
                      correctly: gross - voucher = Total (with GST). Non-voucher
                      bookings are untouched (keep totelPrice as-is). */}
                  {Companycurrency}{' '}
                  {b.couponAmount > 0
                    ? Number(b.finalpriceWithGST || 0) +
                      Number(b.couponAmount || 0) +
                      Number(b.discountValue || 0) -
                      Number(b.addonWithGstTotal || 0) -
                      Number(b.covercharge || 0)
                    : b.totelPrice || 0}
                </td>
              </tr>
              {(b.discountValue > 0 || b.discountType) && (
                <tr>
                  <td
                    style={{
                      padding: '8px 12px',
                      fontSize: 13,
                      borderBottom: '1px solid #f0f0f0',
                      color: '#e53935',
                    }}>
                    Discount ({b.discountType || ''})
                  </td>
                  <td
                    style={{
                      padding: '8px 12px',
                      fontSize: 13,
                      textAlign: 'right',
                      borderBottom: '1px solid #f0f0f0',
                      color: '#e53935',
                    }}>
                    - {Companycurrency} {b.discountValue || 0}
                  </td>
                </tr>
              )}
              {b.addonWithGstTotal > 0 && (
                <tr>
                  <td
                    style={{
                      padding: '8px 12px',
                      fontSize: 13,
                      borderBottom: '1px solid #f0f0f0',
                    }}>
                    Add-on Charges
                  </td>
                  <td
                    style={{
                      padding: '8px 12px',
                      fontSize: 13,
                      textAlign: 'right',
                      borderBottom: '1px solid #f0f0f0',
                    }}>
                    {Companycurrency} {b.addonWithGstTotal}
                  </td>
                </tr>
              )}
              {b.covercharge > 0 && (
                <tr>
                  <td
                    style={{
                      padding: '8px 12px',
                      fontSize: 13,
                      borderBottom: '1px solid #f0f0f0',
                    }}>
                    Cover Charge
                  </td>
                  <td
                    style={{
                      padding: '8px 12px',
                      fontSize: 13,
                      textAlign: 'right',
                      borderBottom: '1px solid #f0f0f0',
                    }}>
                    {Companycurrency} {b.covercharge}
                  </td>
                </tr>
              )}
              {b.couponAmount > 0 && (
                <tr>
                  <td
                    style={{
                      padding: '8px 12px',
                      fontSize: 13,
                      borderBottom: '1px solid #f0f0f0',
                      color: '#43a047',
                    }}>
                    Voucher ({b.coupen})
                  </td>
                  <td
                    style={{
                      padding: '8px 12px',
                      fontSize: 13,
                      textAlign: 'right',
                      borderBottom: '1px solid #f0f0f0',
                      color: '#43a047',
                    }}>
                    - {Companycurrency} {b.couponAmount}
                  </td>
                </tr>
              )}
              <tr style={{ background: '#f5f8ff' }}>
                <td
                  style={{
                    padding: '10px 12px',
                    fontSize: 15,
                    fontWeight: 'bold',
                  }}>
                  {isGstApplicable() ? 'Total (with GST)' : 'Total'}
                </td>
                <td
                  style={{
                    padding: '10px 12px',
                    fontSize: 15,
                    fontWeight: 'bold',
                    textAlign: 'right',
                    color: '#1976d2',
                  }}>
                  {Companycurrency} {b.finalpriceWithGST || 0}
                </td>
              </tr>
              <tr>
                <td
                  style={{
                    padding: '8px 12px',
                    fontSize: 13,
                    color: pending > 0 ? '#d32f2f' : '#2e7d32',
                    fontWeight: 'bold',
                  }}>
                  Pending Amount
                </td>
                <td
                  style={{
                    padding: '8px 12px',
                    fontSize: 13,
                    textAlign: 'right',
                    fontWeight: 'bold',
                    color: pending > 0 ? '#d32f2f' : '#2e7d32',
                  }}>
                  {Companycurrency} {Math.round(pending)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div
          style={{
            background: '#f9f9f9',
            padding: '14px 32px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
          <div style={{ fontSize: 11, color: '#888' }}>
            Thank you for choosing <b>{company?.companyName || 'us'}</b>.
          </div>
          <div style={{ fontSize: 11, color: '#aaa' }}>
            Generated on {fmtDateTime(new Date())}
          </div>
        </div>
      </div>
    );
  };

  const handleOpenManage = (booking) => {
    setSelectedManageBooking(booking);
    setManageDialogOpen(true);
    fetchBookingTimeline(booking?.bookingId);
    fetchManageAddons(booking?.bookingId);
  };

  // Remove ONE room from a multi-room booking (credits its charge back). Called
  // from the Manage Booking dialog's Rooms panel.
  const handleRemoveRoomFromBooking = async (roomNumber) => {
    const b = selectedManageBooking;
    if (!b?.bookingId) return;
    const rooms = String(b.roomNumbers || '')
      .split(',')
      .map((r) => r.trim())
      .filter(Boolean);
    if (rooms.length <= 1) {
      setSnackbar({
        open: true,
        message: 'A booking must keep at least one room.',
        severity: 'warning',
      });
      return;
    }
    if (
      !window.confirm(
        `Remove Room ${roomNumber} from booking #${b.bookingId}? Its charge will be credited back.`,
      )
    )
      return;
    try {
      const token = sessionStorage.getItem('token');
      const res = await fetch(`${config.API_BASE_URL}/bookingroom/remove`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          bookingId: b.bookingId,
          roomNumber: Number(roomNumber),
          companyId: company?.id,
          remark: '',
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || body?.success === false) {
        setSnackbar({
          open: true,
          message: body?.error || 'Failed to remove room',
          severity: 'error',
        });
        return;
      }
      setSnackbar({
        open: true,
        message: body?.message || 'Room removed',
        severity: 'success',
      });
      setManageDialogOpen(false);
      window.location.reload();
    } catch (err) {
      setSnackbar({
        open: true,
        message: 'Failed to remove room',
        severity: 'error',
      });
    }
  };

  // Fetches the booking-action audit rows for the Timeline panel inside the
  // Manage Booking dialog. Non-blocking — the dialog still opens if this fails.
  const fetchBookingTimeline = async (bookingId) => {
    if (!bookingId) {
      setBookingTimeline([]);
      return;
    }
    try {
      setTimelineLoading(true);
      const token = sessionStorage.getItem('token');
      const res = await fetch(`${API_BOOKING_HISTORY_ENDPOINT}/${bookingId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        setBookingTimeline([]);
        return;
      }
      const data = await res.json();
      setBookingTimeline(Array.isArray(data) ? data : []);
    } catch (_) {
      setBookingTimeline([]);
    } finally {
      setTimelineLoading(false);
    }
  };

  const [bookingTimeline, setBookingTimeline] = useState([]);
  const [timelineLoading, setTimelineLoading] = useState(false);

  // Raw addon rows (active + removed) for the Manage Booking dialog. Active
  // rows show a Remove button; removed rows render as a struck-through history
  // line with the timestamp the addon was returned.
  const [manageAddons, setManageAddons] = useState([]);
  const [manageAddonsLoading, setManageAddonsLoading] = useState(false);

  const fetchManageAddons = async (bookingId) => {
    if (!bookingId) {
      setManageAddons([]);
      return;
    }
    try {
      setManageAddonsLoading(true);
      const token = sessionStorage.getItem('token');
      const res = await fetch(`${API_GET_BOOKING_ADDONS}/${bookingId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        setManageAddons([]);
        return;
      }
      const data = await res.json();
      setManageAddons(Array.isArray(data) ? data : []);
    } catch (_) {
      setManageAddons([]);
    } finally {
      setManageAddonsLoading(false);
    }
  };

  const handleRemoveAddon = async (addonId) => {
    if (!addonId) return;
    const ok = window.confirm(
      'Stop billing for this add-on from now?\nRemoved add-ons remain visible in the history.',
    );
    if (!ok) return;
    // Never send a write with userid 0; recover the session first.
    const uid = requireUserId();
    if (!uid) return;
    try {
      setActionText('Removing add-on...');
      setActionLoading(true);
      const token = sessionStorage.getItem('token');
      const url = `${API_REMOVE_ADDON}?addonId=${addonId}&userId=${uid}`;
      const res = await fetch(url, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(await res.text());
      setSnackbar({
        open: true,
        message: 'Add-on removed. Billing stops from now.',
        severity: 'success',
      });
      await fetchManageAddons(selectedManageBooking?.bookingId);
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.message || 'Failed to remove add-on',
        severity: 'error',
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handlePhotoChange = (e) => {
    const files = Array.from(e.target.files);
    const previewFiles = files.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
    }));
    setPhotos((prev) => [...prev, ...previewFiles]);
  };

  // Calls /Home/GetUserIdData (same backend OCR used in ManageInventory).
  // When both Aadhaar sides are present, this auto-fills updateDetails so
  // the admin doesn't retype name/ID/address. Files are still kept in
  // aadharFront/aadharBack so handleSaveUpdate uploads them after save.
  const runBookingAadharOCR = async (frontFile, backFile) => {
    if (!frontFile || !backFile) return;
    try {
      setOcrLoading(true);
      const fd = new FormData();
      fd.append('FrontImage', frontFile);
      fd.append('BackImage', backFile);
      fd.append('Companyid', company?.id);
      const authToken = sessionStorage.getItem('token');
      const res = await fetch(
        `${config.API_BASE_URL}/Home/GetUserIdData`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${authToken}` },
          body: fd,
        },
      );
      if (!res.ok) {
        setSnackbar({
          open: true,
          message: 'Aadhaar OCR failed. Type the details manually.',
          severity: 'warning',
        });
        return;
      }
      const result = await res.json();
      if (result?.code !== 200) {
        setSnackbar({
          open: true,
          message: result?.message || 'Aadhaar OCR could not read the card.',
          severity: 'warning',
        });
        return;
      }
      const front = result?.data?.front || {};
      const back = result?.data?.back || {};
      const fullName = (front?.name || '').trim();
      const nameParts = fullName.split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : '';
      const address = back?.address || '';
      // Best-effort city guess: last comma-separated chunk that isn't just
      // digits / state name. The admin can correct it after.
      const addressParts = address
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      const guessedCity =
        addressParts.length >= 2 ? addressParts[addressParts.length - 2] : '';
      setUpdateDetails((prev) => ({
        ...prev,
        firstName: firstName || prev.firstName,
        lastName: lastName || prev.lastName,
        address: address || prev.address,
        city: guessedCity || prev.city,
        idNumber: front?.aadhaarNumber || prev.idNumber,
        idType: prev.idType || 'Aadhar Card',
      }));
      setSnackbar({
        open: true,
        message: 'Aadhaar OCR captured. Verify the fields before saving.',
        severity: 'success',
      });
    } catch (e) {
      console.error('Aadhaar OCR error:', e);
      setSnackbar({
        open: true,
        message: 'OCR network error.',
        severity: 'error',
      });
    } finally {
      setOcrLoading(false);
    }
  };

  const handleOpenDiscount = (booking) => {
    setCurrentDiscountBooking(booking);
    setDiscountAmount('');
    setDiscountRemark('');
    setDiscountModalOpen(true);
  };

  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [payments, setPayments] = useState([
    { PayMode: '', Amount: '', CardType: '', CardNumber: '', RefNo: '' },
  ]);
  const [Refundpayments, setRefundPayments] = useState([
    { PayMode: '', Amount: '', RefNo: '' },
  ]);
  const [currentBookingPayment, setCurrentBookingPayment] = useState(null);
  const [paymentDetailsOpen, setPaymentDetailsOpen] = useState(false);
  const [selectedBookingPayments, setSelectedBookingPayments] = useState([]);
  const [selectedBookingForPayment, setSelectedBookingForPayment] =
    useState(null);
  const [paymentDetailsLoading, setPaymentDetailsLoading] = useState(false);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [discountDialogOpen, setDiscountDialogOpen] = useState(false);
  const [currentBookingDiscount, setCurrentBookingDiscount] = useState(null);
  const [discountType, setDiscountType] = useState('Percentage');
  const [discountValue, setDiscountValue] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [actionText, setActionText] = useState('');
  const [coverChargeOpen, setCoverChargeOpen] = useState(false);
  const [coverBooking, setCoverBooking] = useState(null);
  const [coverTotalWithGST, setCoverTotalWithGST] = useState('');
  const [coverRemark, setCoverRemark] = useState('');
  const [addPersonOpen, setAddPersonOpen] = useState(false);
  const [addPersonBooking, setAddPersonBooking] = useState(null);
  const [extraAdults, setExtraAdults] = useState(0);
  const [extraChildren, setExtraChildren] = useState(0);
  const [personRemark, setPersonRemark] = useState('');
  const [companyDialogOpen, setCompanyDialogOpen] = useState(false);
  const [companyName, setCompanyName] = useState('');
  const [gstNumber, setGstNumber] = useState('');
  const [lateCheckInOpen, setLateCheckInOpen] = useState(false);
  const [lateCheckInData, setLateCheckInData] = useState({
    amount: '',
    remark: '',
  });
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [checkInFilter, setCheckInFilter] = useState('All');
  const [invoiceFilter, setInvoiceFilter] = useState('All');

  const handleOpenDetails = (booking) => {
    setSelectedBooking(booking);
    setDetailsOpen(true);
  };
  const handleCloseDetails = () => {
    setDetailsOpen(false);
    setSelectedBooking(null);
  };

  const [lateCheckoutOpen, setLateCheckoutOpen] = useState(false);
  const [lateCheckoutBooking, setLateCheckoutBooking] = useState(null);
  const [lateCheckoutData, setLateCheckoutData] = useState({
    amount: '',
    remark: '',
  });
  const [lateBooking, setLateBooking] = useState(null);

  const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
  const [currentUpdateBooking, setCurrentUpdateBooking] = useState(null);
  const [updateDetails, setUpdateDetails] = useState({
    title: '',
    firstName: '',
    lastName: '',
    email: '',
    address: '',
    city: '',
    idNumber: '',
    idType: '',
    idName: '',
    mobileNumber: '',
    checkIn: '',
    checkOut: '',
    companyname: '',
    companyAddress: '',
    companyCity: '',
    companyState: '',
    companyEmail: '',
    companyMobile: '',
    gstno: '',
  });

  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [currentCancelBooking, setCurrentCancelBooking] = useState(null);
  const [cancelRemark, setCancelRemark] = useState('');
  const [cancelrefund, setanCelrefund] = useState('');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const rowsPerPage = 30;
  const company = JSON.parse(sessionStorage.getItem('companyDetails') || '{}');
  const Usersdetails = JSON.parse(sessionStorage.getItem('user') || '{}');
  const Companycurrency = company?.currency ?? '';

  const API_GET_BOOKINGS = `${config.API_BASE_URL}/Home/GetallBookings/${company?.id}`;
  const API_INVOICE_ENDPOINT = `${config.API_BASE_URL}/Home/GenerateInvoice`;
  const API_PAYMENT_ENDPOINT = `${config.API_BASE_URL}/Home/AddPayment`;
  const API_DISCOUNT_ENDPOINT = `${config.API_BASE_URL}/Home/AddDiscount`;
  const API_CHECKIN_ENDPOINT = `${config.API_BASE_URL}/Home/CheckInBooking`;
  const API_UNDO_CHECKIN_ENDPOINT = `${config.API_BASE_URL}/Home/UndoCheckIn`;
  const API_BOOKING_HISTORY_ENDPOINT = `${config.API_BASE_URL}/Home/GetBookingActionHistory`;
  const API_COMPANY_ENDPOINT = `${config.API_BASE_URL}/Home/AddCompanyDetails`;
  const API_UPDATE_DETAILS = `${config.API_BASE_URL}/Home/UpdateBookingDetails`;
  const API_CANCEL_BOOKING = `${config.API_BASE_URL}/Home/CancelBooking`;
  const API_GET_PAYMENT_DETAILS = `${config.API_BASE_URL}/Home/GetPaymentDetails`;
  const API_ADD_DISCOUNT = `${config.API_BASE_URL}/Home/AddDiscount`;
  const API_GET_BOOKING_ADDONS = `${config.API_BASE_URL}/Home/GetBookingAddonsList`;
  const API_REMOVE_ADDON = `${config.API_BASE_URL}/Home/RemoveBookingAddon`;

  const handleViewPayments = async (booking) => {
    setSelectedBookingForPayment(booking);
    setPaymentDetailsLoading(true);
    setPaymentDetailsOpen(true);
    setSelectedBookingPayments([]);
    const token = sessionStorage.getItem('token');
    if (!token) {
      setSnackbar({
        open: true,
        message: 'Authentication failed',
        severity: 'error',
      });
      setPaymentDetailsOpen(false);
      return;
    }
    try {
      const response = await fetch(
        `${API_GET_PAYMENT_DETAILS}/${booking.bookingId}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch: ${response.status} - ${errorText}`);
      }
      const data = await response.json();
      setSelectedBookingPayments(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error(error);
      setSnackbar({
        open: true,
        message: error.message || 'Failed to load payment details',
        severity: 'error',
      });
      setSelectedBookingPayments([]);
    } finally {
      setPaymentDetailsLoading(false);
    }
  };

  const saveBookingAddons = async (bookingId, addonsList) => {
    if (!addonsList || addonsList.length === 0) return;
    try {
      setActionLoading(true);
      setActionText('Saving Add-ons...');
      const token = sessionStorage.getItem('token');
      const payload = {
        BookingId: bookingId,
        Companyid: company?.id,
        Addons: addonsList.map((a) => ({
          Name: a.name.trim(),
          Quantity: Number(a.qty || 1),
          UnitPrice: Number(a.price),
        })),
      };
      const res = await fetch(`${config.API_BASE_URL}/Home/SaveBookingAddons`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(await res.text());
    } catch (err) {
      console.error('Addon save failed:', err);
      setSnackbar({
        open: true,
        message: 'Add-on save failed!',
        severity: 'error',
      });
    } finally {
      setActionLoading(false);
    }
  };

  const normalizeBookings = (data) =>
    Array.isArray(data)
      ? data.map((b) => ({
          ...b,
          price: Math.round(parseFloat(b.price || 0)),
          totelPrice: Math.round(parseFloat(b.totelPrice || 0)),
          finalpriceWithGST: Math.round(parseFloat(b.finalpriceWithGST || 0)),
          pendingAmount: Math.round(parseFloat(b.pendingAmount || 0)),
        }))
      : [];

  const fetchBookings = async () => {
    const token = sessionStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const response = await fetch(API_GET_BOOKINGS, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to fetch bookings');
      const data = await response.json();
      setBookings(normalizeBookings(data));
    } catch (error) {
      console.error('Error fetching bookings:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load bookings',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  // Instant first paint: load just the newest ~60 bookings and render them, THEN
  // pull the full list in the background so search/filters span everything.
  const fetchBookingsFast = async () => {
    const token = sessionStorage.getItem('token');
    if (!token || !company?.id) return;
    try {
      const res = await fetch(
        `${config.API_BASE_URL}/Home/GetallBookingsPaged/${company.id}?skip=0&take=60`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (res.ok) {
        const data = await res.json();
        setBookings(normalizeBookings(data));
        setLoading(false); // show the first page immediately
      }
    } catch {
      /* ignore — the full load below still runs */
    }
  };

  useEffect(() => {
    (async () => {
      await fetchBookingsFast(); // instant first paint (newest 60)
      await fetchBookings(); // full list in the background
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOpenCoverCharge = (booking) => {
    setCoverBooking(booking);
    setCoverTotalWithGST('');
    setCoverRemark('');
    setCoverChargeOpen(true);
  };
  const handleOpenAddPerson = (booking) => {
    setAddPersonBooking(booking);
    setExtraAdults(0);
    setExtraChildren(0);
    setPersonRemark('');
    setAddPersonOpen(true);
  };
  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleSearchChange = (e) => {
    setSearchText(e.target.value);
    setPage(0);
  };

  const filteredBookings = bookings.filter((b) => {
    const textMatch =
      `${b.firstName || ''} ${b.lastName || ''}`
        .toLowerCase()
        .includes(searchText.toLowerCase()) ||
      (b.mobileNumber || '').includes(searchText) ||
      String(b.bookingId || '').includes(searchText);
    const roomMatch =
      !roomFilter ||
      (b.roomNumbers || '').toLowerCase().includes(roomFilter.toLowerCase());
    const sourceMatch =
      sourceFilter === 'All' ||
      (b.through || '').toLowerCase() === sourceFilter.toLowerCase();
    const bookingDate = new Date(b.checkIn);
    bookingDate.setHours(0, 0, 0, 0);
    let fromMatch = true,
      toMatch = true;
    if (fromDate) {
      const from = new Date(fromDate);
      from.setHours(0, 0, 0, 0);
      fromMatch = bookingDate >= from;
    }
    if (toDate) {
      const to = new Date(toDate);
      to.setHours(23, 59, 59, 999);
      toMatch = bookingDate <= to;
    }
    let checkInMatch = true;
    const isCheckedIn = [true, 'true', 1].includes(b.ischeckin);
    if (checkInFilter === 'CheckedIn') checkInMatch = isCheckedIn;
    else if (checkInFilter === 'Pending') checkInMatch = !isCheckedIn;
    let invoiceMatch = true;
    const isInvoiced = [true, 'true', 1].includes(b.isinvoice);
    if (invoiceFilter === 'Generated') invoiceMatch = isInvoiced;
    else if (invoiceFilter === 'NotGenerated') invoiceMatch = !isInvoiced;
    return (
      textMatch &&
      roomMatch &&
      sourceMatch &&
      fromMatch &&
      toMatch &&
      checkInMatch &&
      invoiceMatch
    );
  });

  const sortedBookings = [...filteredBookings].sort((a, b) => {
    if (a.isCancel && !b.isCancel) return 1;
    if (!a.isCancel && b.isCancel) return -1;
    const aInvoice = [true, 'true', 1].includes(a.isinvoice);
    const bInvoice = [true, 'true', 1].includes(b.isinvoice);
    return aInvoice - bInvoice;
  });

  const handleSaveDiscount = async () => {
    if (!currentDiscountBooking) return;
    const amount = Number(discountAmount);
    if (isNaN(amount) || amount <= 0) {
      setSnackbar({
        open: true,
        message: 'Please enter a valid discount amount greater than 0',
        severity: 'warning',
      });
      return;
    }
    if (!discountRemark.trim()) {
      setSnackbar({
        open: true,
        message: 'Remark is required for discount',
        severity: 'warning',
      });
      return;
    }
    try {
      setActionLoading(true);
      setActionText('Applying discount...');
      const payload = {
        BookingId: currentDiscountBooking.bookingId,
        DiscountType: 'Ruppe',
        DiscountValue: amount,
        DiscountAmount: amount,
        Remark: discountRemark.trim(),
      };
      const token = sessionStorage.getItem('token');
      const response = await fetch(API_ADD_DISCOUNT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        // Backend's BadRequest returns { message: "..." } — surface it cleanly
        // instead of dumping raw JSON into the snackbar.
        const raw = await response.text();
        let friendly = raw;
        try {
          const parsed = JSON.parse(raw);
          friendly = parsed?.message || raw;
        } catch (_) {}
        throw new Error(friendly || 'Failed to apply discount');
      }
      setSnackbar({
        open: true,
        message: 'Discount applied successfully!',
        severity: 'success',
      });
      setDiscountModalOpen(false);
      setDiscountAmount('');
      setDiscountRemark('');
      setCurrentDiscountBooking(null);
      window.location.reload();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.message || 'Failed to apply discount',
        severity: 'error',
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleCheckIn = async (bookingId) => {
    const booking = bookings.find((b) => b.bookingId === bookingId);
    if (!booking) {
      setSnackbar({
        open: true,
        message: 'Booking not found!',
        severity: 'error',
      });
      return;
    }
    // Block check-in until guest ID details are recorded. Bookings save with
    // just name + mobile (relaxed at booking time), so we enforce ID capture
    // here at the check-in gate. Either a native ID (type+number) or a
    // foreign ID (passport+visa) is acceptable.
    const hasNativeId =
      (booking.idType || '').toString().trim() !== '' &&
      (booking.idNumber || '').toString().trim() !== '';
    const hasForeignId =
      (booking.passportNumber || '').toString().trim() !== '' &&
      (booking.visaNumber || '').toString().trim() !== '';
    if (!hasNativeId && !hasForeignId) {
      setSnackbar({
        open: true,
        message:
          'Guest ID details missing. Open Update and add ID Type + ID Number (or Passport + Visa) before check-in.',
        severity: 'warning',
      });
      return;
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const checkInDate = new Date(booking.checkIn);
    checkInDate.setHours(0, 0, 0, 0);
    const checkOutDate = new Date(booking.checkOut);
    checkOutDate.setHours(0, 0, 0, 0);
    if (today < checkInDate) {
      setSnackbar({
        open: true,
        message: `Check-in allowed from ${checkInDate.toLocaleDateString('en-IN')}`,
        severity: 'warning',
      });
      return;
    }
    if (today > checkOutDate) {
      setSnackbar({
        open: true,
        message: `Check-in not allowed after ${checkOutDate.toLocaleDateString('en-IN')}`,
        severity: 'warning',
      });
      return;
    }
    const now = new Date();
    const currentTime = now.toTimeString().slice(0, 5);
    const allowedCheckInTime = booking.checkInTime;
    if (allowedCheckInTime && currentTime < allowedCheckInTime) {
      const wantCharge = window.confirm(
        `You are checking in early.\nAllowed check-in time is ${allowedCheckInTime}.\nDo you want to add early check-in charges?`,
      );
      if (wantCharge) {
        // Ask for the actual charge amount instead of silently sending 0
        // (which checked the guest in with no early-check-in charge at all).
        const input = window.prompt(
          'Enter early check-in charge amount (₹):',
          '',
        );
        if (input === null) return; // amount prompt cancelled → don't check in
        const amount = Number(input);
        if (!Number.isFinite(amount) || amount < 0) {
          setSnackbar({
            open: true,
            message: 'Please enter a valid early check-in charge amount.',
            severity: 'warning',
          });
          return;
        }
        await doCheckInApi(
          bookingId,
          amount > 0 ? { amount, remark: 'Early check-in charge' } : null,
        );
        return;
      }
      await doCheckInApi(bookingId, null);
      return;
    }
    const confirmCheckIn = window.confirm(
      'Are you sure you want to check-in this booking?',
    );
    if (!confirmCheckIn) return;
    await doCheckInApi(bookingId, null);
  };

  // #5: undo the early check-in charge for a booking (removes the addon so the
  // pending no longer includes it). Refreshes the bookings list afterwards.
  const removeEarlyCheckinCharge = async (bookingId) => {
    if (!bookingId) return;
    if (!window.confirm('Remove the early check-in charge from this booking?'))
      return;
    try {
      const token = sessionStorage.getItem('token');
      const res = await fetch(
        `${config.API_BASE_URL}/bookingledger/RemoveEarlyCheckinCharge/${bookingId}`,
        { method: 'POST', headers: { Authorization: `Bearer ${token}` } },
      );
      const data = await res.json().catch(() => ({}));
      setSnackbar({
        open: true,
        message: data?.message || 'Done.',
        severity: data?.removed > 0 ? 'success' : 'info',
      });
      setPaymentDetailsOpen(false);
      fetchBookings();
    } catch (e) {
      setSnackbar({
        open: true,
        message: 'Could not remove the early check-in charge.',
        severity: 'error',
      });
    }
  };

  const handleExportExcel = () => {
    if (!filteredBookings.length) {
      setSnackbar({
        open: true,
        message: 'No data to export',
        severity: 'warning',
      });
      return;
    }
    const excelData = filteredBookings.map((b) => ({
      'Booking ID': b.bookingId,
      'Hotel Name': b.hotelName,
      'Room Type': b.roomType,
      'Room Numbers': b.roomNumbers,
      'Check In Date': new Date(b.checkIn).toLocaleDateString('en-IN'),
      'Check Out Date': new Date(b.checkOut).toLocaleDateString('en-IN'),
      'Check In Time': b.checkInTime,
      'Check Out Time': b.checkOutTime,
      Title: b.title || '',
      'First Name': b.firstName,
      'Last Name': b.lastName,
      Email: b.email,
      Mobile: b.mobileNumber,
      Address: b.address,
      City: b.city,
      'ID Type': b.idType,
      'ID Number': b.idNumber,
      'Booking Source': b.through,
      'Adult Count': b.adultCount,
      'Child Count': b.childCount,
      Price: b.price,
      'Total Price': b.totelPrice,
      'GST Amount': b.finalpriceWithGST,
      'Pending Amount': b.pendingAmount,
      'Discount Type': b.discountType,
      'Discount Value': b.discountValue,
      'Coupon Code': b.coupen,
      'Coupon Amount': b.couponAmount,
      'Is Checked In': b.ischeckin ? 'Yes' : 'No',
      'Is Invoiced': b.isinvoice ? 'Yes' : 'No',
      'Is Cancelled': b.isCancel ? 'Yes' : 'No',
      'Cancel Remark': b.cancelRemark || '',
      'Company Name': b.companyname || '',
      'Created Date': b.created
        ? new Date(b.created).toLocaleString('en-IN')
        : '',
    }));
    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Bookings');
    const excelBuffer = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array',
    });
    const data = new Blob([excelBuffer], { type: 'application/octet-stream' });
    saveAs(
      data,
      `Booking_Report_${new Date().toISOString().slice(0, 10)}.xlsx`,
    );
  };

  const doCheckInApi = async (
    bookingId,
    lateData,
    forcedCheckInTime = null,
  ) => {
    try {
      setActionText('Checking in...');
      setActionLoading(true);
      const token = sessionStorage.getItem('token');
      // #1: send the early-checkin charge ONLY when it's a real positive number.
      // 0 / blank / null all become 0 so the backend never creates a charge addon
      // (it only inserts the "Early Checkin Charge" row when lateCharge != 0).
      const lateCharge =
        Number(lateData?.amount) > 0 ? Number(lateData.amount) : 0;
      const remark = lateData?.remark || '';
      const url = `${API_CHECKIN_ENDPOINT}?bookingId=${bookingId}&lateCharge=${lateCharge}&remark=${encodeURIComponent(remark)}${forcedCheckInTime ? `&checkInTime=${encodeURIComponent(forcedCheckInTime)}` : ''}`;
      const res = await fetch(url, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(await res.text());
      setSnackbar({
        open: true,
        message: 'Check-In Successful!',
        severity: 'success',
      });
      window.location.reload();
    } catch (error) {
      setSnackbar({ open: true, message: error.message, severity: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  // Reverts a check-in. Confirms with the user first, calls /UndoCheckIn,
  // then reloads so the booking row reflects the new (un-checked-in) state.
  const handleUndoCheckIn = async (bookingId) => {
    return runUndoAction(
      bookingId,
      API_UNDO_CHECKIN_ENDPOINT,
      'check-in',
      'Reverting check-in...',
    );
  };

  const handleUndoDiscount = async (bookingId) => {
    return runUndoAction(
      bookingId,
      `${config.API_BASE_URL}/Home/UndoDiscount`,
      'discount',
      'Reverting discount...',
    );
  };

  const handleUndoCoverCharge = async (bookingId) => {
    return runUndoAction(
      bookingId,
      `${config.API_BASE_URL}/Home/UndoCoverCharge`,
      'cover charge',
      'Reverting cover charge...',
    );
  };

  const handleUndoInvoice = async (bookingId) => {
    return runUndoAction(
      bookingId,
      `${config.API_BASE_URL}/Home/UndoInvoice`,
      'invoice',
      'Reverting invoice...',
    );
  };

  // Shared undo runner — confirm + POST + snackbar + reload.
  const runUndoAction = async (bookingId, endpoint, label, progressText) => {
    if (!bookingId) return;
    const ok = window.confirm(
      `Are you sure you want to undo this ${label}?\nThis operation will restore previous booking data.`,
    );
    if (!ok) return;
    // Never send a write with userid 0; recover the session first.
    const uid = requireUserId();
    if (!uid) return;
    try {
      setActionText(progressText);
      setActionLoading(true);
      const token = sessionStorage.getItem('token');
      const url = `${endpoint}?bookingId=${bookingId}&userId=${uid}`;
      const res = await fetch(url, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const raw = await res.text();
        let friendly = raw;
        try {
          const parsed = JSON.parse(raw);
          friendly = parsed?.message || raw;
        } catch (_) {}
        throw new Error(friendly || 'Undo failed');
      }
      setSnackbar({
        open: true,
        message: `Action reverted successfully.`,
        severity: 'success',
      });
      window.location.reload();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.message || 'Undo failed',
        severity: 'error',
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleGenerateInvoice = async (booking) => {
    if (!booking.ischeckin) {
      setSnackbar({
        open: true,
        message: 'Check-in required first',
        severity: 'warning',
      });
      return;
    }
    try {
      const hotelId = booking.hotelId || company?.hotelId || company?.id;
      const r = await laundryApi.listOrders(hotelId, { pageSize: 500 });
      const pending = (r.data?.items || []).filter(
        (o) =>
          o.bookingId === booking.bookingId &&
          o.status !== 'Delivered' &&
          o.status !== 'Cancelled',
      );
      if (pending.length > 0) {
        const list = pending
          .map((o) => `${o.orderNo} (${o.status})`)
          .join(', ');
        const proceed = window.confirm(
          `Laundry order${pending.length > 1 ? 's' : ''} still pending for this booking:\n${list}\n\nGenerate invoice anyway?`,
        );
        if (!proceed) return;
      }
    } catch (e) {
      console.error('Laundry pending check failed', e);
    }
    const now = new Date();
    let allowedTill, expectedCheckout;
    const is24HourMode = booking.is24Hour === true;
    if (is24HourMode) {
      const checkInDateTime = new Date(booking.checkIn);
      const checkOutDateTime = new Date(booking.checkOut);
      const diffMs = checkOutDateTime.getTime() - checkInDateTime.getTime();
      const days = Math.max(1, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));
      const totalHours = days * 24;
      expectedCheckout = new Date(checkInDateTime);
      expectedCheckout.setHours(expectedCheckout.getHours() + totalHours);
      allowedTill = new Date(expectedCheckout);
      allowedTill.setHours(allowedTill.getHours() + 2);
    } else {
      expectedCheckout = new Date(booking.checkOut);
      const [hh, mm] = (booking.checkOutTime || '12:00').split(':');
      expectedCheckout.setHours(parseInt(hh, 10), parseInt(mm, 10), 0, 0);
      allowedTill = new Date(expectedCheckout);
      allowedTill.setHours(allowedTill.getHours() + 2);
    }
    if (parseFloat(booking.pendingAmount || 0) > 0) {
      const confirmPending = window.confirm(
        `${Companycurrency} ${booking.pendingAmount} is still pending.\nDo you still want to generate the invoice?`,
      );
      if (!confirmPending) return;
    }
    const confirmMsg = is24HourMode
      ? 'Confirm checkout & generate invoice?\n(24-hour booking based on actual check-in time)'
      : 'Are you sure you want to checkout and generate invoice?';
    if (!window.confirm(confirmMsg)) return;
    setBookingForInvoice(booking);
    const isLate = now > expectedCheckout;
    if (isLate) {
      const lateMsg = is24HourMode
        ? `Late checkout detected.\nStay ended at ${fmtDateTime(expectedCheckout)}.\nDo you want to add late checkout charges?`
        : `Checkout time has passed.\nDo you want to add late checkout charges?`;
      const wantCharge = window.confirm(lateMsg);
      if (wantCharge) {
        setLateCheckoutBooking(booking);
        setLateCheckoutData({ amount: '', remark: '' });
        setLateCheckoutOpen(true);
        return;
      }
    }
    submitInvoice(booking, []);
  };

  const submitInvoice = async (booking, addonsList = []) => {
    try {
      setActionLoading(true);
      setActionText('Generating invoice...');
      const lateCheckoutCharge = Number(lateCheckoutData?.amount || 0);
      const lateCheckoutRemark = lateCheckoutData?.remark?.trim() || '';
      const formattedAddons = addonsList
        .filter(
          (a) =>
            a.name?.trim() && Number(a.price) > 0 && Number(a.qty || 1) >= 1,
        )
        .map((a) => ({
          Name: a.name.trim(),
          Price: Number(a.price),
          Quantity: Number(a.qty || 1),
        }));
      const totalAddonsAmount = formattedAddons.reduce(
        (sum, a) => sum + a.Price,
        0,
      );
      const payload = {
        BookingId: booking.bookingId,
        HotelId: booking.hotelId || 0,
        RoomId: booking.roomId || 0,
        Price: Number(booking.price || 0),
        Companyid: company?.id,
        TotelPrice:
          Number(booking.totelPrice || 0) +
          totalAddonsAmount +
          lateCheckoutCharge,
        AdultCount: Number(booking.adultCount || 0),
        ChildCount: Number(booking.childCount || 0),
        Checkin: booking.checkIn,
        checkOut: booking.checkOut,
        RoomNumbers: booking.roomNumbers || '',
        Addons: formattedAddons,
        LateCheckoutCharge: lateCheckoutCharge,
        LateCheckoutRemark: lateCheckoutRemark,
      };
      const token = sessionStorage.getItem('token');
      if (!token) throw new Error('Authentication token not found');
      const res = await fetch(API_INVOICE_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok)
        throw new Error((await res.text()) || `Server error: ${res.status}`);
      setSnackbar({
        open: true,
        message: 'Invoice generated successfully!',
        severity: 'success',
      });
      setLateCheckoutData({ amount: '', remark: '' });
      setLateCheckoutOpen(false);
      setAddonInputOpen(false);
      setAddons([{ Name: '', Price: '' }]);
      setBookingForInvoice(null);
      window.location.reload();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.message || 'Failed to generate invoice',
        severity: 'error',
      });
    } finally {
      setActionLoading(false);
    }
  };

  // Preview must mirror the generated invoice (InvoicePage.js), which reads
  // `totelPrice` directly from `Getinvoicedata`. Computing GST/discount/cover
  // ourselves here previously drifted from the saved total because GST percent
  // was hardcoded and `discountTypepre/discountValuepre` carry the preliminary
  // discount, not the applied one in `discountType/discountValue`.
  const roomTotalWithGST = Number(
    selectedManageBooking?.finalpriceWithGST || 0,
  );
  // Company GST switch OFF → no GST split anywhere on the preview bill.
  const gstOn = isGstApplicable();
  const roomGstPercent = !gstOn ? 0 : roomTotalWithGST < 7875 ? 5 : 18;
  const roomBasePrice = roomTotalWithGST / (1 + roomGstPercent / 100);
  const roomGSTAmount = roomTotalWithGST - roomBasePrice;
  const coverChargeWithGST = Number(selectedManageBooking?.covercharge || 0);
  const coverBaseAmount = gstOn ? coverChargeWithGST / 1.05 : coverChargeWithGST;
  const coverGSTAmount = coverChargeWithGST - coverBaseAmount;
  const totalBaseAmount = roomBasePrice + coverBaseAmount;
  const totalGST = roomGSTAmount + coverGSTAmount;
  const totalWithGST = roomTotalWithGST + coverChargeWithGST;
  const addonAmount = Number(selectedManageBooking?.addonWithGstTotal || 0);
  const appliedDiscountType =
    selectedManageBooking?.discountType ||
    selectedManageBooking?.discountTypepre ||
    '';
  const appliedDiscountValue = Number(
    selectedManageBooking?.discountValue ||
      selectedManageBooking?.discountValuepre ||
      0,
  );
  const invoiceDiscountAmount =
    appliedDiscountType.toLowerCase() === 'percentage'
      ? (totalWithGST * appliedDiscountValue) / 100
      : appliedDiscountValue;
  // Anchor preview's grand total to the server-saved `totelPrice` (room
  // amount) PLUS the separately-stored cover charge. `totelPrice` from the
  // booking list does NOT fold in cover (unlike Getinvoicedata used by the
  // final invoice), so add `coverChargeWithGST` here — otherwise the preview
  // shows only the room amount (e.g. 1200) instead of room + cover (2000).
  const grandTotal = Math.round(
    Number(selectedManageBooking?.totelPrice || 0) + coverChargeWithGST,
  );

  const handleAddonChange = (index, field, value) => {
    const updated = [...addons];
    updated[index][field] = value;
    setAddons(updated);
  };
  const handleAddAddonRow = () =>
    setAddons([...addons, { name: '', price: '', qty: '1' }]);
  const handleRemoveAddonRow = (index) => {
    if (addons.length === 1) return;
    setAddons(addons.filter((_, i) => i !== index));
  };
  const handleSaveAddons = () => {
    submitInvoice(
      currentBooking,
      addons.filter((a) => a.Name && a.Price),
    );
    setAddonDialogOpen(false);
  };

  const handleAddPayment = (booking) => {
    setCurrentBookingPayment(booking);
    setPayments([
      { PayMode: '', Amount: '', CardType: '', CardNumber: '', RefNo: '' },
    ]);
    setPaymentDialogOpen(true);
  };
  const handlePaymentChange = (i, field, val) => {
    const updated = [...payments];
    updated[i][field] = val;
    setPayments(updated);
  };
  const handleAddPaymentRow = () =>
    setPayments([
      ...payments,
      { PayMode: '', Amount: '', CardType: '', CardNumber: '', RefNo: '' },
    ]);
  const handleRemovePaymentRow = (i) =>
    setPayments(payments.filter((_, idx) => idx !== i));

  const handleSavePayments = async () => {
    const validPayments = payments.filter((p) => p.PayMode && p.Amount > 0);
    if (validPayments.length === 0) return;
    // Never write a payment row with userid 0; recover the session first.
    const uid = requireUserId();
    if (!uid) return;
    try {
      setActionText('Saving payment...');
      setActionLoading(true);
      const payload = {
        BookingId: currentBookingPayment.bookingId,
        Companyid: company?.id,
        Userid: uid,
        Payments: validPayments.map((p) => ({
          PayMode: p.PayMode,
          Amount: parseFloat(p.Amount),
          CardType: p.CardType || null,
          CardNumber: p.CardNumber || null,
          RefNo: p.RefNo || null,
        })),
      };
      const token = sessionStorage.getItem('token');
      const res = await fetch(API_PAYMENT_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error();
      setSnackbar({
        open: true,
        message: 'Payment added!',
        severity: 'success',
      });
      setPaymentDialogOpen(false);
      window.location.reload();
      setCurrentBooking(currentBookingPayment);
    } catch {
      setSnackbar({ open: true, message: 'Payment failed', severity: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddDiscount = (booking) => {
    setCurrentBookingDiscount(booking);
    setDiscountType('Percentage');
    setDiscountValue('');
    setDiscountDialogOpen(true);
  };

  const handleOpenUpdate = (booking) => {
    setCurrentUpdateBooking(booking);
    setAadharFront(null);
    setAadharBack(null);
    setUpdateAdultCount(Number(booking.adultCount) || 1);
    setUpdateChildCount(Number(booking.childCount) || 0);
    setUpdateOtherGuests([]);
    // Fetch existing co-guests so the dialog shows them in editable rows.
    // Non-blocking — the rest of the dialog renders immediately and the
    // co-guest list pops in once the response arrives.
    (async () => {
      try {
        const authToken = sessionStorage.getItem('token');
        const res = await fetch(
          `${config.API_BASE_URL}/Home/GetBookingOtherGuests/${booking.bookingId}`,
          { headers: { Authorization: `Bearer ${authToken}` } },
        );
        if (!res.ok) return;
        const data = await res.json();
        if (Array.isArray(data)) {
          setUpdateOtherGuests(
            data.map((g) => ({
              title: g.title || '',
              firstName: g.firstName || '',
              lastName: g.lastName || '',
              mobileNumber: g.mobileNumber || '',
            })),
          );
        }
      } catch (e) {
        console.error('GetBookingOtherGuests failed:', e);
      }
    })();
    // Legacy bookings store titles without the trailing dot (e.g. "Mr"),
    // but our Select uses the dotted forms ("Mr."). Normalise so the
    // Select doesn't warn about an out-of-range value.
    const normalizedTitle = (() => {
      const t = (booking.title || '').toString().trim();
      if (!t) return '';
      const direct = TITLE_OPTIONS.find(
        (o) => o.toLowerCase() === t.toLowerCase(),
      );
      if (direct) return direct;
      const dotted = TITLE_OPTIONS.find(
        (o) => o.toLowerCase() === `${t.toLowerCase()}.`,
      );
      return dotted || '';
    })();
    setUpdateDetails({
      title: normalizedTitle,
      firstName: booking.firstName || '',
      lastName: booking.lastName || '',
      email: booking.email || '',
      address: booking.address || '',
      city: booking.city || '',
      idNumber: booking.idNumber || '',
      idType: booking.idType || '',
      idName: booking.idName || '',
      mobileNumber: booking.mobileNumber || '',
      companyname: booking.companyname || '',
      companyAddress: booking.companyAddress || '',
      companyCity: booking.companyCity || '',
      companyState: booking.companyState || '',
      companyEmail: booking.companyEmail || '',
      companyMobile: booking.companyMobile || '',
      gstno: booking.gstno || '',
      // Keep DATE + TIME (datetime-local format "YYYY-MM-DDTHH:mm") so the
      // front desk can edit the exact check-in / check-out time, not just the
      // date. The backend already persists the full DateTime.
      checkIn: booking.checkIn ? booking.checkIn.slice(0, 16) : '',
      checkOut: booking.checkOut ? booking.checkOut.slice(0, 16) : '',
    });
    setUpdateDialogOpen(true);
  };

  const handleSaveUpdate = async () => {
    // Date fields are only editable BEFORE check-in. Once Ischeckin=1 the
    // stay window is locked and we echo the saved CheckIn/CheckOut back to
    // the backend unchanged. Date sanity checks must therefore run only on
    // the not-yet-checked-in path — otherwise editing user/company details
    // for an already checked-out (invoiced) guest gets blocked by a
    // "checkout date is in the past" alert that is irrelevant to the edit.
    const isCheckedInOrOut =
      [true, 'true', 1].includes(currentUpdateBooking.ischeckin) ||
      [true, 'true', 1].includes(currentUpdateBooking.isinvoice);
    if (!isCheckedInOrOut) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      // Full check-out date+time (datetime-local). Compare the DATE only for
      // the "not in the past" rule, but keep the time for the order check so a
      // same-day later-time check-out (e.g. in 14:00, out 18:00) isn't blocked.
      const newCheckOut = new Date(updateDetails.checkOut);
      const checkOutDateOnly = new Date(newCheckOut);
      checkOutDateOnly.setHours(0, 0, 0, 0);
      if (checkOutDateOnly < today) {
        setSnackbar({
          open: true,
          message: 'Checkout date cannot be in the past',
          severity: 'error',
        });
        return;
      }
      if (updateDetails.checkIn) {
        const checkIn = new Date(updateDetails.checkIn);
        if (newCheckOut <= checkIn) {
          setSnackbar({
            open: true,
            message: 'Checkout must be after check-in',
            severity: 'warning',
          });
          return;
        }
      }
    }

    // #4: VOUCHER ON EXTENSION. If this booking carries a voucher and the stay
    // is being EXTENDED, ask the front desk whether the voucher should also
    // cover the newly added days. OK = keep the voucher on the new days
    // (default); Cancel = bill the new nights at full rate. The choice rides
    // along to the backend (ApplyVoucherToNewDays) and is stored in history.
    let applyVoucherToNewDays = true;
    const hasVoucher =
      Number(currentUpdateBooking.couponAmount) > 0 ||
      !!currentUpdateBooking.coupen;
    const isInvoiced = [true, 'true', 1].includes(
      currentUpdateBooking.isinvoice,
    );
    if (hasVoucher && !isInvoiced && updateDetails.checkOut) {
      const oldOut = new Date(currentUpdateBooking.checkOut);
      const newOut = new Date(updateDetails.checkOut);
      oldOut.setHours(0, 0, 0, 0);
      newOut.setHours(0, 0, 0, 0);
      if (newOut > oldOut) {
        applyVoucherToNewDays = window.confirm(
          'This booking has an active voucher.\n\n' +
            'Apply the voucher to the newly added days too?\n\n' +
            'OK = Yes, keep the voucher on the new days\n' +
            'Cancel = No, charge the new days at full rate',
        );
      }
    }

    try {
      setActionText('Updating booking...');
      setActionLoading(true);
      const payload = {
        BookingId: currentUpdateBooking.bookingId,
        ApplyVoucherToNewDays: applyVoucherToNewDays,
        Title: updateDetails.title,
        FirstName: updateDetails.firstName,
        LastName: updateDetails.lastName,
        Email: updateDetails.email,
        Address: updateDetails.address,
        City: updateDetails.city,
        IdNumber: updateDetails.idNumber,
        IdType: updateDetails.idType,
        IdName: updateDetails.idName,
        MobileNumber: updateDetails.mobileNumber,
        CompanyName: updateDetails.companyname,
        CompanyAddress: updateDetails.companyAddress,
        CompanyCity: updateDetails.companyCity,
        CompanyState: updateDetails.companyState,
        CompanyEmail: updateDetails.companyEmail,
        CompanyMobile: updateDetails.companyMobile,
        Gstno: updateDetails.gstno,
        // Once Ischeckin=1 the stay window is locked; echo the saved values back
        // so the backend never receives a stale/edited date for a checked-in guest.
        // CheckIn locked once Ischeckin=1 or Isinvoice=1 — echo the saved
        // value back so the backend never receives a stale/edited date.
        // CheckOut stays editable AFTER check-in (front desk can extend
        // the stay); only locked when the invoice has already been issued.
        CheckIn:
          [true, 'true', 1].includes(currentUpdateBooking.ischeckin) ||
          [true, 'true', 1].includes(currentUpdateBooking.isinvoice)
            ? currentUpdateBooking.checkIn
            : updateDetails.checkIn,
        CheckOut: [true, 'true', 1].includes(currentUpdateBooking.isinvoice)
          ? currentUpdateBooking.checkOut
          : updateDetails.checkOut,
        DiscountType: updateDiscountType,
        DiscountValue: Number(updateDiscountValue || 0),
        AdultCount: Number(updateAdultCount) || 1,
        ChildCount: Number(updateChildCount) || 0,
        // Trim blank rows on the way out — backend re-filters anyway, but
        // sending only real entries keeps the audit cleaner.
        OtherGuests: (updateOtherGuests || [])
          .filter(
            (g) =>
              (g.firstName || '').trim() || (g.mobileNumber || '').trim(),
          )
          .map((g) => ({
            Title: g.title || '',
            FirstName: g.firstName || '',
            LastName: g.lastName || '',
            MobileNumber: g.mobileNumber || '',
          })),
      };
      const token = sessionStorage.getItem('token');
      const res = await fetch(API_UPDATE_DETAILS, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error();

      // Persist Aadhaar front/back if the user picked any. Multipart, fired
      // after the JSON details update so a network-flaky upload doesn't
      // block the textual edits. Non-blocking — the dialog still closes
      // and the snackbar reports success even if the photo POST fails.
      if (aadharFront || aadharBack) {
        try {
          const fd = new FormData();
          fd.append('BookingId', String(currentUpdateBooking.bookingId));
          if (aadharFront) fd.append('FrontImage', aadharFront);
          if (aadharBack) fd.append('BackImage', aadharBack);
          const upRes = await fetch(
            `${config.API_BASE_URL}/Home/UploadBookingAadhar`,
            {
              method: 'POST',
              headers: { Authorization: `Bearer ${token}` },
              body: fd,
            },
          );
          if (!upRes.ok) {
            setSnackbar({
              open: true,
              message: 'Details saved but Aadhaar upload failed.',
              severity: 'warning',
            });
          }
        } catch (e) {
          console.error('Aadhaar upload error:', e);
        }
      }

      setSnackbar({
        open: true,
        message: 'Details updated!',
        severity: 'success',
      });
      setUpdateDialogOpen(false);
      window.location.reload();
    } catch {
      setSnackbar({ open: true, message: 'Update failed', severity: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleOpenCancel = (booking) => {
    setCurrentCancelBooking(booking);
    setCancelRemark('');
    setanCelrefund('');
    setCancelDialogOpen(true);
  };

  const handleConfirmCancel = async () => {
    if (!cancelRemark.trim()) {
      setSnackbar({
        open: true,
        message: 'Cancellation remark is required!',
        severity: 'warning',
      });
      return;
    }
    if (!currentCancelBooking) {
      setSnackbar({
        open: true,
        message: 'Booking not found!',
        severity: 'error',
      });
      return;
    }
    if (!Refundpayments.PayMode) {
      setSnackbar({
        open: true,
        message: 'Please select pay mode!',
        severity: 'warning',
      });
      return;
    }
    try {
      setActionText('Cancelling booking...');
      setActionLoading(true);
      const bookingId = currentCancelBooking.bookingId;
      const remark = encodeURIComponent(cancelRemark.trim());
      const refundAmount = Refundpayments.Amount || 0;
      const payMode = Refundpayments.PayMode;
      const refNo = Refundpayments.RefNo || 'NA';
      const token = sessionStorage.getItem('token');
      const url = `${API_CANCEL_BOOKING}/${bookingId}/${remark}/${refundAmount}/${payMode}/${refNo}`;
      const res = await fetch(url, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(await res.text());
      setSnackbar({
        open: true,
        message: 'Booking cancelled successfully!',
        severity: 'success',
      });
      setCancelDialogOpen(false);
      setCancelRemark('');
      setRefundPayments({ PayMode: '', Amount: '', RefNo: '' });
      window.location.reload();
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleViewInvoice = (booking) => {
    window.open(
      `${window.location.origin}/invoice/${booking.bookingId}`,
      '_blank',
    );
  };
  const handlePreviewInvoice = (booking) => {
    setSelectedManageBooking(booking);
    setInvoicePreviewOpen(true);
    fetchPreviewAddons(booking);
  };

  // Fetch the booking's saved addons so the Preview Invoice can render
  // each line. The backend's `/Home/Getinvoicedata/{bookingId}/{companyId}`
  // returns an array of rows carrying comma-separated `AddonNamesRaw` +
  // `AddonPricesRaw` (and a `Addonflegs` discriminator). We zip them into
  // a `[{ name, price, fleg }]` array for the table renderer.
  const fetchPreviewAddons = async (booking) => {
    if (!booking?.bookingId || !company?.id) {
      setPreviewAddons([]);
      return;
    }
    try {
      const token = sessionStorage.getItem('token');
      const res = await fetch(
        `${config.API_BASE_URL}/Home/Getinvoicedata/${booking.bookingId}/${company.id}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) {
        setPreviewAddons([]);
        return;
      }
      const arr = await res.json();
      const row = Array.isArray(arr) && arr.length > 0 ? arr[0] : null;
      if (!row) {
        setPreviewAddons([]);
        return;
      }
      // Server may already split into arrays OR return the raw CSV strings.
      // Handle both shapes.
      const names =
        Array.isArray(row.addonNames) && row.addonNames.length
          ? row.addonNames
          : String(row.addonNamesRaw || row.AddonNamesRaw || '')
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean);
      const prices =
        Array.isArray(row.addonPrices) && row.addonPrices.length
          ? row.addonPrices
          : String(row.addonPricesRaw || row.AddonPricesRaw || '')
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean);
      const flegs =
        Array.isArray(row.addonflegs) && row.addonflegs.length
          ? row.addonflegs
          : String(row.addonflegRaw || row.AddonflegRaw || '')
              .split(',')
              .map((s) => s.trim());
      const merged = names.map((n, i) => ({
        name: n,
        price: Number(prices[i] || 0),
        fleg: flegs[i] || '',
      }));
      setPreviewAddons(merged);
    } catch (err) {
      console.error('fetchPreviewAddons failed:', err);
      setPreviewAddons([]);
    }
  };

  const handleSaveCoverCharge = async () => {
    if (!coverBooking) return;
    const amountWithGST = Number(coverTotalWithGST);
    if (amountWithGST <= 0) return;
    let gstRate = amountWithGST < 7875 ? 5 : 18;
    const payload = {
      bookingId: coverBooking.bookingId,
      coverCharge: amountWithGST,
      gstRate: gstRate,
      remark: coverRemark.trim() || 'Cover charge added',
    };
    try {
      setActionText('Saving payment...');
      setActionLoading(true);
      const token = sessionStorage.getItem('token');
      const res = await fetch(`${config.API_BASE_URL}/Home/AddCoverCharge`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const raw = await res.text();
        let friendly = raw;
        try {
          const parsed = JSON.parse(raw);
          friendly = parsed?.message || raw;
        } catch (_) {}
        throw new Error(friendly);
      }
      setSnackbar({
        open: true,
        message: 'Cover charge added successfully',
        severity: 'success',
      });
      setCoverChargeOpen(false);
      setCoverTotalWithGST('');
      setCoverRemark('');
      setActionLoading(false);
      window.location.reload();
    } catch (err) {
      setSnackbar({
        open: true,
        message: 'Failed to add cover charge: ' + err.message,
        severity: 'error',
      });
    }
  };

  if (loading) {
    return (
      <Box
        display='flex'
        justifyContent='center'
        alignItems='center'
        minHeight='70vh'
        flexDirection='column'
        gap={2}>
        <CircularProgress size={56} thickness={4} sx={{ color: '#1976d2' }} />
        <Typography color='text.secondary' fontSize={14}>
          Loading bookings...
        </Typography>
      </Box>
    );
  }

  // ─── Shared header cell style ────────────────────────────────────────────
  const thSx = {
    backgroundColor: '#1565c0',
    color: '#fff',
    fontWeight: 700,
    fontSize: '12px',
    position: 'sticky',
    top: 0,
    zIndex: 10,
    border: '1px solid #1976d2',
    padding: '11px 12px',
    textAlign: 'center',
    whiteSpace: 'nowrap',
    letterSpacing: 0.3,
    textTransform: 'uppercase',
  };

  const bookingColW = {
    bookingId: '7%',
    guest: '14%',
    room: '5%',
    checkIn: '10%',
    checkOut: '11%',
    status: '7%',
    pending: '8%',
    username: '9%',
    manage: '8%',
    amount: '11%',
  };

  const formatStayCell = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return '—';
    return (
      <Box sx={{ lineHeight: 1.25 }}>
        <Typography
          component='span'
          sx={{ display: 'block', fontSize: 12, fontWeight: 500 }}>
          {d.toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: '2-digit',
          })}
        </Typography>
        <Typography
          component='span'
          sx={{ display: 'block', fontSize: 11, color: 'text.secondary' }}>
          {d.toLocaleTimeString('en-IN', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true,
          })}
        </Typography>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        p: { xs: 1.25, sm: 2, md: 2.5 },
        bgcolor: '#f0f4f8',
        minHeight: '100vh',
        overflowX: 'hidden',
      }}>
      {/* ─── Page Header ──────────────────────────────────────────────────── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 3,
          flexWrap: 'wrap',
          gap: 2,
        }}>
        <Box>
          <Typography
            variant='h5'
            fontWeight='bold'
            color='#1565c0'
            letterSpacing={0.3}
            sx={{ fontSize: { xs: '1.1rem', sm: '1.5rem' } }}>
            Manage Bookings
          </Typography>
          <Typography fontSize={13} color='text.secondary' mt={0.3}>
            {filteredBookings.length} booking
            {filteredBookings.length !== 1 ? 's' : ''} found
          </Typography>
        </Box>
        <Button
          variant='contained'
          color='success'
          onClick={handleExportExcel}
          size={isMobile ? 'small' : 'medium'}
          sx={{
            fontWeight: 'bold',
            px: { xs: 2, sm: 3 },
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(46,125,50,0.25)',
            textTransform: 'none',
            fontSize: { xs: 12, sm: 14 },
          }}>
          Export to Excel
        </Button>
      </Box>

      {/* ─── Filters ──────────────────────────────────────────────────────── */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 3,
          borderRadius: 3,
          border: '1px solid #e3eaf3',
          bgcolor: '#fff',
          display: 'flex',
          gap: 1.5,
          flexWrap: 'wrap',
          alignItems: 'center',
        }}>
        <TextField
          type='date'
          size='small'
          label='From Date'
          InputLabelProps={{ shrink: true }}
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          sx={{
            minWidth: { xs: 'calc(50% - 6px)', sm: 150 },
            flex: { xs: '1 1 calc(50% - 6px)', sm: 'unset' },
          }}
        />
        <TextField
          type='date'
          size='small'
          label='To Date'
          InputLabelProps={{ shrink: true }}
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          sx={{
            minWidth: { xs: 'calc(50% - 6px)', sm: 150 },
            flex: { xs: '1 1 calc(50% - 6px)', sm: 'unset' },
          }}
        />
        <TextField
          label='Search Name / Mobile / ID'
          variant='outlined'
          size='small'
          value={searchText}
          onChange={handleSearchChange}
          sx={{
            minWidth: { xs: '100%', sm: 240 },
            flex: { xs: '1 1 100%', sm: 'unset' },
          }}
        />
        <TextField
          select
          size='small'
          label='Check-in Status'
          value={checkInFilter}
          onChange={(e) => setCheckInFilter(e.target.value)}
          sx={{
            minWidth: { xs: 'calc(50% - 6px)', sm: 150 },
            flex: { xs: '1 1 calc(50% - 6px)', sm: 'unset' },
          }}>
          <MenuItem value='All'>All</MenuItem>
          <MenuItem value='CheckedIn'>Checked In</MenuItem>
          <MenuItem value='Pending'>Pending</MenuItem>
        </TextField>
        <TextField
          select
          size='small'
          label='Invoice Status'
          value={invoiceFilter}
          onChange={(e) => setInvoiceFilter(e.target.value)}
          sx={{
            minWidth: { xs: 'calc(50% - 6px)', sm: 150 },
            flex: { xs: '1 1 calc(50% - 6px)', sm: 'unset' },
          }}>
          <MenuItem value='All'>All</MenuItem>
          <MenuItem value='Generated'>Generated</MenuItem>
          <MenuItem value='NotGenerated'>Not Generated</MenuItem>
        </TextField>
        <TextField
          size='small'
          label='Room No'
          value={roomFilter}
          onChange={(e) => setRoomFilter(e.target.value)}
          sx={{
            minWidth: { xs: 'calc(50% - 6px)', sm: 100 },
            flex: { xs: '1 1 calc(50% - 6px)', sm: 'unset' },
          }}
        />
        <TextField
          select
          size='small'
          label='Source'
          value={sourceFilter}
          onChange={(e) => setSourceFilter(e.target.value)}
          sx={{
            minWidth: { xs: 'calc(50% - 6px)', sm: 130 },
            flex: { xs: '1 1 calc(50% - 6px)', sm: 'unset' },
          }}>
          <MenuItem value='All'>All</MenuItem>
          <MenuItem value='Walkin'>Walkin</MenuItem>
          <MenuItem value='Agent'>Agent</MenuItem>
        </TextField>
        <Button
          variant='outlined'
          color='secondary'
          onClick={() => {
            setFromDate('');
            setToDate('');
            setSearchText('');
            setRoomFilter('');
            setSourceFilter('All');
            setCheckInFilter('All');
            setInvoiceFilter('All');
          }}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: 2,
            flex: { xs: '1 1 100%', sm: 'unset' },
          }}>
          Clear
        </Button>
      </Paper>

      {/* ─── Main Table ───────────────────────────────────────────────────── */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          overflow: 'hidden',
          border: '1px solid #e3eaf3',
          mb: 4,
        }}>

        {/* ── MOBILE CARD LIST (replaces the wide table on phones) ── */}
        {isMobile && (
          <Box sx={{ p: 1.2, display: 'flex', flexDirection: 'column', gap: 1.2 }}>
            {sortedBookings.length === 0 ? (
              <Typography align='center' color='text.secondary' sx={{ py: 6 }}>
                No bookings found
              </Typography>
            ) : (
              sortedBookings
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((b) => {
                  const isCheckedIn = [true, 'true', 1].includes(b.ischeckin);
                  const isInvoiced = [true, 'true', 1].includes(b.isinvoice);
                  const isCancelled = !!b.isCancel;
                  const pending = parseFloat(b.pendingAmount || 0);
                  const status = isCancelled
                    ? { t: 'Cancelled', c: '#c62828', bg: '#ffebee' }
                    : isInvoiced
                      ? { t: 'Invoiced', c: '#2e7d32', bg: '#e8f5e9' }
                      : isCheckedIn
                        ? { t: 'Checked In', c: '#1565c0', bg: '#e3f2fd' }
                        : { t: 'Booked', c: '#e65100', bg: '#fff3e0' };
                  const fmt = (d) =>
                    d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : '-';
                  return (
                    <Box
                      key={b.bookingId}
                      sx={{
                        border: '1px solid #e3eaf3',
                        borderRadius: 2,
                        p: 1.3,
                        bgcolor: isCancelled ? '#fff5f5' : '#fff',
                      }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography sx={{ fontWeight: 800, color: '#1565c0', fontSize: 14 }}>
                          #{b.bookingId}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                          <Box sx={{ fontSize: 10, fontWeight: 700, px: 0.9, py: 0.2, borderRadius: 1, color: status.c, bgcolor: status.bg }}>
                            {status.t}
                          </Box>
                          <Typography sx={{ fontWeight: 800, fontSize: 14, color: pending > 0 ? '#d32f2f' : '#2e7d32' }}>
                            {Companycurrency}{Math.round(pending)}
                          </Typography>
                        </Box>
                      </Box>
                      <Typography sx={{ fontSize: 14, fontWeight: 600, mt: 0.5 }}>
                        {(b.title ? b.title + ' ' : '') + (b.firstName || '') + ' ' + (b.lastName || '')}
                      </Typography>
                      <Typography sx={{ fontSize: 12.5, color: '#64748b' }}>
                        🛏️ Room {b.roomNumbers ? roomLabel(b.roomNumbers) : '—'} &nbsp;·&nbsp; 📅 {fmt(b.checkIn)} → {fmt(b.checkOut)}
                      </Typography>
                      {b.through && (
                        <Typography sx={{ fontSize: 11.5, color: '#94a3b8' }}>{b.through} · {b.username || ''}</Typography>
                      )}
                      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                        <Button size='small' variant='contained' onClick={() => handleOpenManage(b)}
                          sx={{ textTransform: 'none', flex: 1 }}>
                          Manage
                        </Button>
                        {!isCheckedIn && !isCancelled && !isInvoiced && (
                          <Button size='small' variant='outlined' color='primary'
                            onClick={() => handleCheckIn(b.bookingId)}
                            sx={{ textTransform: 'none', flex: 1 }}>
                            Check In
                          </Button>
                        )}
                      </Box>
                    </Box>
                  );
                })
            )}
          </Box>
        )}

        {!isMobile && (
        <TableContainer
          sx={{ maxHeight: '75vh', overflowX: { xs: 'auto', md: 'hidden' } }}>
          <Table
            stickyHeader
            size='small'
            sx={{
              tableLayout: { xs: 'auto', md: 'fixed' },
              width: '100%',
              minWidth: { xs: 1100, md: 'unset' },
              borderCollapse: 'collapse',
              '& th, & td': {
                border: '1px solid #e8edf3',
                padding: { xs: '6px 4px', sm: '10px 8px' },
                textAlign: 'center',
                fontSize: { xs: '11px', sm: '13px' },
              },
              '& td': {
                bgcolor: 'background.paper',
                overflow: 'hidden',
              },
              '& tr:hover td': { bgcolor: '#e8f4ff !important' },
              '& tr:nth-of-type(even) td': { bgcolor: '#f7fafd' },
            }}>
            <TableHead>
              <TableRow>
                <TableCell
                  align='center'
                  sx={{
                    ...thSx,
                    width: bookingColW.bookingId,
                    padding: '10px 6px',
                  }}>
                  Booking ID
                </TableCell>
                <TableCell
                  align='center'
                  sx={{
                    ...thSx,
                    width: bookingColW.guest,
                    padding: '10px 6px',
                  }}>
                  Guest Name
                </TableCell>
                <TableCell
                  align='center'
                  sx={{ ...thSx, width: bookingColW.room }}>
                  Room
                </TableCell>
                <TableCell
                  align='center'
                  sx={{ ...thSx, width: bookingColW.checkIn }}>
                  Check-In
                </TableCell>
                <TableCell
                  align='center'
                  sx={{ ...thSx, width: bookingColW.checkOut }}>
                  Check-Out
                </TableCell>
                <TableCell
                  align='center'
                  sx={{ ...thSx, width: bookingColW.status }}>
                  Status
                </TableCell>
                <TableCell
                  align='center'
                  sx={{ ...thSx, width: bookingColW.pending }}>
                  Pending
                </TableCell>
                <TableCell
                  align='center'
                  sx={{ ...thSx, width: bookingColW.username }}>
                  Username
                </TableCell>
                <TableCell
                  align='center'
                  sx={{ ...thSx, width: bookingColW.manage }}>
                  Manage
                </TableCell>
                <TableCell
                  align='center'
                  sx={{
                    ...thSx,
                    width: bookingColW.amount,
                    bgcolor: '#0d47a1 !important',
                    borderLeft: '2px solid #1565c0',
                  }}>
                  Amount
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {sortedBookings.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align='center' sx={{ py: 10 }}>
                    <Typography variant='h6' color='text.secondary'>
                      No bookings found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                sortedBookings
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((b) => {
                    const isCheckedIn = [true, 'true', 1].includes(b.ischeckin);
                    const isInvoiced = [true, 'true', 1].includes(b.isinvoice);
                    const isCancelled = !!b.isCancel;
                    const pending = parseFloat(b.pendingAmount || 0);
                    const rowBg = isCancelled ? '#fff5f5' : undefined;

                    return (
                      <TableRow
                        key={b.bookingId}
                        hover
                        sx={{ opacity: isCancelled ? 0.78 : 1 }}>
                        {/* Booking ID */}
                        <TableCell
                          sx={{
                            bgcolor: rowBg || 'background.paper',
                            fontWeight: 'bold',
                            fontSize: 13,
                            color: '#1565c0',
                          }}>
                          #{b.bookingId}
                        </TableCell>

                        {/* Guest Name */}
                        <TableCell
                          sx={{
                            bgcolor: rowBg || 'background.paper',
                            textAlign: 'left !important',
                          }}>
                          <Box
                            display='flex'
                            alignItems='center'
                            gap={0.75}
                            minWidth={0}>
                            <Avatar
                              sx={{
                                width: 28,
                                height: 28,
                                fontSize: 12,
                                fontWeight: 'bold',
                                bgcolor: isCancelled
                                  ? '#ffcdd2'
                                  : isInvoiced
                                    ? '#c8e6c9'
                                    : isCheckedIn
                                      ? '#bbdefb'
                                      : '#fff9c4',
                                color: isCancelled
                                  ? '#c62828'
                                  : isInvoiced
                                    ? '#2e7d32'
                                    : isCheckedIn
                                      ? '#1565c0'
                                      : '#e65100',
                                flexShrink: 0,
                              }}>
                              {(b.firstName || 'G').charAt(0).toUpperCase()}
                            </Avatar>
                            <Box minWidth={0} sx={{ overflow: 'hidden' }}>
                              <Typography
                                fontSize={13}
                                fontWeight={600}
                                lineHeight={1.2}
                                noWrap
                                title={`${b.title ? `${b.title} ` : ''}${b.firstName} ${b.lastName || ''}`.trim()}>
                                {b.title ? `${b.title} ` : ''}
                                {b.firstName}
                              </Typography>
                              <Typography
                                fontSize={11}
                                color='text.secondary'
                                noWrap
                                title={b.lastName}>
                                {b.lastName}
                              </Typography>
                              {b.through && (
                                <Chip
                                  label={b.through}
                                  size='small'
                                  variant='outlined'
                                  color={
                                    (b.through || '').toLowerCase() === 'agent'
                                      ? 'secondary'
                                      : 'primary'
                                  }
                                  sx={{
                                    height: 16,
                                    fontSize: 9,
                                    mt: 0.25,
                                    '& .MuiChip-label': { px: 0.6 },
                                  }}
                                />
                              )}
                            </Box>
                          </Box>
                        </TableCell>

                        {/* Room No */}
                        <TableCell>
                          <Typography fontWeight='bold' fontSize={13}>
                            {roomLabel(b.roomNumbers)}
                          </Typography>
                        </TableCell>

                        {/* Check-In */}
                        <TableCell sx={{ fontSize: 12 }}>
                          {formatStayCell(b.checkIn)}
                        </TableCell>

                        {/* Check-Out */}
                        <TableCell sx={{ fontSize: 12 }}>
                          <Box
                            display='flex'
                            flexDirection='column'
                            alignItems='center'
                            justifyContent='center'
                            gap={0.25}>
                            {formatStayCell(b.checkOut)}
                            <Tooltip
                              title={
                                isInvoiced
                                  ? 'Invoice Generated'
                                  : 'Invoice Not Generated'
                              }>
                              {isInvoiced ? (
                                <CheckCircle
                                  sx={{ color: '#43a047', fontSize: 16 }}
                                />
                              ) : (
                                <Cancel
                                  sx={{ color: '#e53935', fontSize: 16 }}
                                />
                              )}
                            </Tooltip>
                          </Box>
                        </TableCell>

                        {/* Status */}
                        <TableCell>
                          {isCancelled ? (
                            <Cancel color='error' fontSize='small' />
                          ) : isCheckedIn ? (
                            <Box
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.3,
                              }}>
                              <Tooltip title='Checked In'>
                                <CheckCircle
                                  sx={{ color: '#43a047' }}
                                  fontSize='small'
                                />
                              </Tooltip>
                              {!isInvoiced && (
                                <Tooltip title='Undo Check-In'>
                                  <IconButton
                                    size='small'
                                    onClick={() =>
                                      handleUndoCheckIn(b.bookingId)
                                    }
                                    sx={{
                                      p: 0.2,
                                      color: '#ef6c00',
                                      '&:hover': { bgcolor: '#FFF3E0' },
                                    }}>
                                    <UndoIcon sx={{ fontSize: 14 }} />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                          ) : hasPermission(P.BOOKING_CHECKIN) ? (
                            <Button
                              size='small'
                              variant='outlined'
                              onClick={() => handleCheckIn(b.bookingId)}
                              sx={{
                                fontSize: 10,
                                px: 1,
                                py: 0.3,
                                minWidth: 0,
                                textTransform: 'none',
                                borderRadius: 1.5,
                                fontWeight: 600,
                                color: '#1565c0',
                                borderColor: '#90caf9',
                              }}>
                              Check In
                            </Button>
                          ) : (
                            <Typography
                              variant='caption'
                              color='text.secondary'>
                              —
                            </Typography>
                          )}
                        </TableCell>

                        {/* Pending */}
                        <TableCell>
                          {pending > 0 ? (
                            <Box
                              display='flex'
                              flexDirection='column'
                              alignItems='center'
                              gap={0.2}>
                              <Typography
                                fontSize={13}
                                fontWeight='bold'
                                color='#d32f2f'>
                                {Companycurrency}
                                {Math.round(pending)}
                              </Typography>
                              <Typography fontSize={10} color='#e57373'>
                                Due
                              </Typography>
                            </Box>
                          ) : (
                            <Typography
                              fontSize={13}
                              fontWeight='bold'
                              color='#2e7d32'>
                              ✓ Paid
                            </Typography>
                          )}
                        </TableCell>

                        {/* Username */}
                        <TableCell>
                          <Typography
                            fontSize={13}
                            color='text.secondary'
                            noWrap
                            title={b.name || 'Admin'}>
                            {b.name || 'Admin'}
                          </Typography>
                        </TableCell>

                        {/* Manage */}
                        <TableCell>
                          {hasPermission(P.BOOKING_VIEW) && (
                            <Button
                              variant='contained'
                              size='small'
                              onClick={() => handleOpenManage(b)}
                              sx={{
                                fontSize: 11,
                                px: 1.5,
                                py: 0.5,
                                minWidth: 0,
                                whiteSpace: 'nowrap',
                                bgcolor: '#1565c0',
                                fontWeight: 700,
                                borderRadius: 1.5,
                                textTransform: 'none',
                                boxShadow: 'none',
                                '&:hover': {
                                  bgcolor: '#0d47a1',
                                  boxShadow: 'none',
                                },
                              }}>
                              Manage
                            </Button>
                          )}
                        </TableCell>

                        {/* Amount Details */}
                        <TableCell sx={{ borderLeft: '2px solid #bbdefb' }}>
                          <Tooltip title='Amount Details'>
                            <Button
                              variant='outlined'
                              size='small'
                              onClick={() => handleOpenPaymentDrawer(b)}
                              sx={{
                                fontSize: 11,
                                px: 1,
                                py: 0.5,
                                minWidth: 0,
                                color: '#1565c0',
                                borderColor: '#90caf9',
                                textTransform: 'none',
                                fontWeight: 600,
                                borderRadius: 1.5,
                                '&:hover': {
                                  bgcolor: '#e3f2fd',
                                  borderColor: '#1565c0',
                                },
                              }}>
                              <PaymentOutlined sx={{ fontSize: 15 }} />
                            </Button>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        )}

        <Box sx={{ bgcolor: '#fff', borderTop: '1px solid #e8edf3' }}>
          <TablePagination
            rowsPerPageOptions={[30]}
            component='div'
            count={sortedBookings.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            labelRowsPerPage='Rows per page:'
            sx={{
              '.MuiTablePagination-toolbar': { fontSize: '13px' },
              '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows':
                { fontSize: '13px' },
            }}
          />
        </Box>
      </Paper>

      {/* ─── Payment Details Drawer ──────────────────────────────────────── */}
      <PaymentDetailsDrawer
        open={paymentDrawerOpen}
        onClose={() => setPaymentDrawerOpen(false)}
        booking={drawerBooking}
        currency={Companycurrency}
        roomLabel={roomLabel}
      />

      {/* ─── Spa / Laundry / Mini-Bar services breakdown for a booking ──── */}
      <Dialog
        open={servicesOpen}
        onClose={() => setServicesOpen(false)}
        maxWidth='lg'
        fullWidth
        fullScreen={isMobile}>
        <DialogTitle>
          {servicesView === 'restaurant' ? 'Restaurant Orders' : 'Services'} for
          Booking #{servicesData.booking?.bookingId}
          {servicesData.booking?.roomNumbers
            ? ` — Room ${roomLabel(servicesData.booking.roomNumbers)}`
            : ''}
        </DialogTitle>
        <DialogContent dividers>
          {servicesLoading && <CircularProgress size={28} />}
          {!servicesLoading && (() => {
            const spaTotal = servicesData.spa.reduce(
              (s, x) => s + Number(x.totalAmount || 0),
              0,
            );
            const laundryTotal = servicesData.laundry.reduce(
              (s, x) => s + Number(x.totalAmount || 0),
              0,
            );
            const minibarTotal = servicesData.minibar.reduce(
              (s, x) => s + Number(x.lineTotal || 0),
              0,
            );
            const restaurantTotal = servicesData.restaurant.reduce(
              (s, x) => s + Number(x.finalPrice || 0),
              0,
            );
            // The dialog is reused for two views; totals + empty-state are
            // scoped to whichever view is active so each button shows only its
            // own data (restaurant is now a separate button).
            const grand =
              servicesView === 'restaurant'
                ? restaurantTotal
                : spaTotal + laundryTotal + minibarTotal;
            const noData =
              servicesView === 'restaurant'
                ? servicesData.restaurant.length === 0
                : servicesData.spa.length +
                    servicesData.laundry.length +
                    servicesData.minibar.length ===
                  0;
            return (
              <Box>
                {noData && (
                  <Typography color='text.secondary' sx={{ py: 2 }}>
                    {servicesView === 'restaurant'
                      ? 'No restaurant orders for this booking yet.'
                      : 'No spa / laundry / mini-bar orders for this booking yet.'}
                  </Typography>
                )}

                {/* Spa — header row + nested service items under each booking */}
                {servicesView === 'services' && servicesData.spa.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant='subtitle1' fontWeight='700' color='primary' gutterBottom>
                      Spa Bookings
                    </Typography>
                    <Table size='small'>
                      <TableHead>
                        <TableRow>
                          <TableCell>Spa #</TableCell>
                          <TableCell>Slot</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell align='right'>Subtotal</TableCell>
                          <TableCell align='right'>GST</TableCell>
                          <TableCell align='right'>Discount</TableCell>
                          <TableCell align='right'>Total</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {servicesData.spa.map((s) => (
                          <React.Fragment key={s.spaBookingId}>
                            <TableRow sx={{ bgcolor: '#f5faff' }}>
                              <TableCell>{s.spaBookingId}</TableCell>
                              <TableCell>{new Date(s.slotStart).toLocaleString('en-GB')}</TableCell>
                              <TableCell>{s.status}</TableCell>
                              <TableCell align='right'>{Number(s.subTotal).toFixed(2)}</TableCell>
                              <TableCell align='right'>{Number(s.gstAmount).toFixed(2)}</TableCell>
                              <TableCell align='right'>{Number(s.discountAmount).toFixed(2)}</TableCell>
                              <TableCell align='right'>
                                <strong>{Number(s.totalAmount).toFixed(2)}</strong>
                              </TableCell>
                            </TableRow>
                            {(s.items || []).map((it, i) => (
                              <TableRow key={`spa-${s.spaBookingId}-item-${i}`}>
                                <TableCell />
                                <TableCell colSpan={2} sx={{ pl: 4 }}>
                                  • {it.serviceName || `Service #${it.serviceId}`}
                                  {it.therapistName ? ` (Therapist: ${it.therapistName})` : ''}
                                  {it.durationMinutes ? ` — ${it.durationMinutes} min` : ''}
                                </TableCell>
                                <TableCell align='right'>{Number(it.unitPrice).toFixed(2)}</TableCell>
                                <TableCell align='right'>{Number(it.gstAmount).toFixed(2)}</TableCell>
                                <TableCell />
                                <TableCell align='right'>{Number(it.lineTotal).toFixed(2)}</TableCell>
                              </TableRow>
                            ))}
                          </React.Fragment>
                        ))}
                        <TableRow>
                          <TableCell colSpan={6} align='right'><strong>Spa Total</strong></TableCell>
                          <TableCell align='right'>
                            <strong>{Companycurrency} {spaTotal.toFixed(2)}</strong>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </Box>
                )}

                {/* Laundry — header row + nested item rows under each order */}
                {servicesView === 'services' && servicesData.laundry.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant='subtitle1' fontWeight='700' color='primary' gutterBottom>
                      Laundry Orders
                    </Typography>
                    <Table size='small'>
                      <TableHead>
                        <TableRow>
                          <TableCell>Order #</TableCell>
                          <TableCell>Date</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell align='right'>Qty</TableCell>
                          <TableCell align='right'>Unit Price</TableCell>
                          <TableCell align='right'>GST</TableCell>
                          <TableCell align='right'>Discount</TableCell>
                          <TableCell align='right'>Total</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {servicesData.laundry.map((o) => (
                          <React.Fragment key={o.laundryOrderId}>
                            <TableRow sx={{ bgcolor: '#f5faff' }}>
                              <TableCell>{o.orderNo}</TableCell>
                              <TableCell>{new Date(o.orderDate).toLocaleString('en-GB')}</TableCell>
                              <TableCell>{o.status}</TableCell>
                              <TableCell align='right'>—</TableCell>
                              <TableCell align='right'>{Number(o.subTotal).toFixed(2)}</TableCell>
                              <TableCell align='right'>{Number(o.gstAmount).toFixed(2)}</TableCell>
                              <TableCell align='right'>{Number(o.discountAmount).toFixed(2)}</TableCell>
                              <TableCell align='right'>
                                <strong>{Number(o.totalAmount).toFixed(2)}</strong>
                              </TableCell>
                            </TableRow>
                            {(o.items || []).map((it, i) => (
                              <TableRow key={`lnd-${o.laundryOrderId}-item-${i}`}>
                                <TableCell />
                                <TableCell colSpan={2} sx={{ pl: 4 }}>
                                  • {it.itemName || `Item #${it.laundryItemId}`}
                                  {it.washType ? ` (${it.washType})` : ''}
                                </TableCell>
                                <TableCell align='right'>{it.quantity}</TableCell>
                                <TableCell align='right'>{Number(it.unitPrice).toFixed(2)}</TableCell>
                                <TableCell align='right'>{Number(it.gstAmount).toFixed(2)}</TableCell>
                                <TableCell />
                                <TableCell align='right'>{Number(it.lineTotal).toFixed(2)}</TableCell>
                              </TableRow>
                            ))}
                          </React.Fragment>
                        ))}
                        <TableRow>
                          <TableCell colSpan={7} align='right'><strong>Laundry Total</strong></TableCell>
                          <TableCell align='right'>
                            <strong>{Companycurrency} {laundryTotal.toFixed(2)}</strong>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </Box>
                )}

                {/* Mini-Bar */}
                {servicesView === 'services' && servicesData.minibar.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant='subtitle1' fontWeight='700' color='primary' gutterBottom>
                      Mini-Bar Consumption
                    </Typography>
                    <Table size='small'>
                      <TableHead>
                        <TableRow>
                          <TableCell>When</TableCell>
                          <TableCell>Item</TableCell>
                          <TableCell>Tax</TableCell>
                          <TableCell align='right'>Qty</TableCell>
                          <TableCell align='right'>Unit Price</TableCell>
                          <TableCell align='right'>Tax Amt</TableCell>
                          <TableCell align='right'>Total</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {servicesData.minibar.map((c) => {
                          const tax = (c.taxType || 'GST').toUpperCase();
                          return (
                            <TableRow key={c.consumptionId}>
                              <TableCell>{new Date(c.consumedAt).toLocaleString('en-GB')}</TableCell>
                              <TableCell>{c.productName}</TableCell>
                              <TableCell>
                                <Chip size='small'
                                  label={`${tax} ${Number(c.gstPercent || 0)}%`}
                                  color={tax === 'VAT' ? 'warning' : 'primary'}
                                  variant='outlined' />
                              </TableCell>
                              <TableCell align='right'>{c.quantity}</TableCell>
                              <TableCell align='right'>{Number(c.unitPrice).toFixed(2)}</TableCell>
                              <TableCell align='right'>{Number(c.gstAmount).toFixed(2)}</TableCell>
                              <TableCell align='right'>
                                <strong>{Number(c.lineTotal).toFixed(2)}</strong>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                        <TableRow>
                          <TableCell colSpan={6} align='right'><strong>Mini-Bar Total</strong></TableCell>
                          <TableCell align='right'>
                            <strong>{Companycurrency} {minibarTotal.toFixed(2)}</strong>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </Box>
                )}

                {/* Restaurant — header row + lazy-loaded item rows on expand */}
                {servicesView === 'restaurant' && servicesData.restaurant.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant='subtitle1' fontWeight='700' color='primary' gutterBottom>
                      Restaurant Orders
                    </Typography>
                    <Table size='small'>
                      <TableHead>
                        <TableRow>
                          <TableCell />
                          <TableCell>Order #</TableCell>
                          <TableCell>Date</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell align='right'>Subtotal</TableCell>
                          <TableCell align='right'>GST</TableCell>
                          <TableCell align='right'>VAT</TableCell>
                          <TableCell align='right'>Discount</TableCell>
                          <TableCell align='right'>Total</TableCell>
                          <TableCell align='right'>Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {servicesData.restaurant.map((o) => {
                          const expanded = !!restroItems[o.id];
                          const entry = restroItems[o.id];
                          return (
                            <React.Fragment key={o.id}>
                              <TableRow sx={{ bgcolor: '#f5faff' }}>
                                <TableCell>
                                  <Tooltip title={expanded ? 'Hide items' : 'View items'}>
                                    <IconButton size='small' onClick={() => toggleRestroItems(o.id)}>
                                      {expanded ? <ExpandLess /> : <ExpandMore />}
                                    </IconButton>
                                  </Tooltip>
                                </TableCell>
                                <TableCell>{o.id}</TableCell>
                                <TableCell>{fmtDateSafe(o.createdate || o.createDate)}</TableCell>
                                <TableCell>
                                  <Chip size='small'
                                    label={o.isInvoice ? 'Invoiced' : 'Open'}
                                    color={o.isInvoice ? 'info' : 'warning'} />
                                </TableCell>
                                <TableCell align='right'>{Number(o.price).toFixed(2)}</TableCell>
                                <TableCell align='right'>{Number(o.gstAmount).toFixed(2)}</TableCell>
                                <TableCell align='right'>{Number(o.vatamount || o.vATAMount || 0).toFixed(2)}</TableCell>
                                <TableCell align='right'>{Number(o.dicountamount).toFixed(2)}</TableCell>
                                <TableCell align='right'>
                                  <strong>{Number(o.finalPrice).toFixed(2)}</strong>
                                </TableCell>
                                <TableCell align='right'>
                                  <Tooltip title='Open restaurant invoice'>
                                    <IconButton size='small' onClick={() =>
                                      window.open(`${window.location.origin}/RestroInvoice/${o.id}`, '_blank')
                                    }>
                                      <ReceiptLongOutlined fontSize='small' />
                                    </IconButton>
                                  </Tooltip>
                                </TableCell>
                              </TableRow>
                              {expanded && (
                                <TableRow>
                                  <TableCell />
                                  <TableCell colSpan={9} sx={{ p: 0, bgcolor: '#fafafa' }}>
                                    {entry?.loading && (
                                      <Box sx={{ p: 1 }}><CircularProgress size={18} /></Box>
                                    )}
                                    {entry && !entry.loading && (entry.items || []).length === 0 && (
                                      <Box sx={{ p: 1, color: 'text.secondary' }}>
                                        No items found for this order.
                                      </Box>
                                    )}
                                    {entry && !entry.loading && (entry.items || []).length > 0 && (
                                      <Table size='small'>
                                        <TableHead>
                                          <TableRow>
                                            <TableCell sx={{ pl: 4 }}>Item</TableCell>
                                            <TableCell>Category</TableCell>
                                            <TableCell>KOT</TableCell>
                                            <TableCell align='right'>Qty</TableCell>
                                            <TableCell align='right'>Unit Price</TableCell>
                                            <TableCell align='right'>GST</TableCell>
                                            <TableCell align='right'>VAT</TableCell>
                                            <TableCell align='right'>Line Total</TableCell>
                                          </TableRow>
                                        </TableHead>
                                        <TableBody>
                                          {(entry.items || []).map((it, i) => (
                                            <TableRow key={i}>
                                              <TableCell sx={{ pl: 4 }}>• {it.name}</TableCell>
                                              <TableCell>{it.category_name || '-'}</TableCell>
                                              <TableCell>{it.kotno || '-'}</TableCell>
                                              <TableCell align='right'>{it.qty}</TableCell>
                                              <TableCell align='right'>{Number(it.price || 0).toFixed(2)}</TableCell>
                                              <TableCell align='right'>{Number(it.gstAmount || 0).toFixed(2)}</TableCell>
                                              <TableCell align='right'>
                                                {it.isvataplicable ? Number(it.vatrate || 0).toFixed(2) : '-'}
                                              </TableCell>
                                              <TableCell align='right'>
                                                <strong>{Number(it.totalPrice || it.finalPrice || 0).toFixed(2)}</strong>
                                              </TableCell>
                                            </TableRow>
                                          ))}
                                        </TableBody>
                                      </Table>
                                    )}
                                  </TableCell>
                                </TableRow>
                              )}
                            </React.Fragment>
                          );
                        })}
                        <TableRow>
                          <TableCell colSpan={8} align='right'><strong>Restaurant Total</strong></TableCell>
                          <TableCell align='right'>
                            <strong>{Companycurrency} {restaurantTotal.toFixed(2)}</strong>
                          </TableCell>
                          <TableCell />
                        </TableRow>
                      </TableBody>
                    </Table>
                  </Box>
                )}

                {!noData && (
                  <Box display='flex' justifyContent='flex-end' mt={2}>
                    <Typography variant='h6'>
                      Services Grand Total:&nbsp;
                      <strong>{Companycurrency} {grand.toFixed(2)}</strong>
                    </Typography>
                  </Box>
                )}
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setServicesOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ─── Booking Confirmation Slip Dialog ──────────────────────────── */}
      <Dialog
        open={confirmationSlipOpen}
        onClose={() => setConfirmationSlipOpen(false)}
        maxWidth='md'
        fullWidth
        fullScreen={isMobile}>
        <DialogTitle
          sx={{
            bgcolor: '#1976d2',
            color: '#fff',
            fontWeight: 'bold',
            textAlign: 'center',
            fontSize: 18,
          }}>
          Booking Confirmation Slip
        </DialogTitle>
        <DialogContent sx={{ bgcolor: '#dfe3e6', p: 3 }}>
          <Box sx={{ maxWidth: 900, mx: 'auto' }}>
            <ConfirmationSlipContent b={confirmationBooking} />
          </Box>
        </DialogContent>
        <DialogActions
          sx={[
            { p: 2, gap: 1, justifyContent: 'flex-end' },
            RESPONSIVE_DIALOG_ACTIONS_SX,
          ]}>
          <Button
            variant='outlined'
            onClick={() => setConfirmationSlipOpen(false)}>
            Close
          </Button>
          <Button
            variant='contained'
            color='info'
            onClick={handleDownloadConfirmationPDF}>
            Download PDF
          </Button>
          <Button
            variant='contained'
            color='primary'
            onClick={handlePrintConfirmation}>
            Print Slip
          </Button>
        </DialogActions>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog
        open={paymentDialogOpen}
        onClose={() => setPaymentDialogOpen(false)}
        maxWidth='sm'
        fullWidth
        fullScreen={isMobile}>
        <DialogTitle>Add Payment</DialogTitle>
        <DialogContent dividers>
          {payments.map((p, i) => (
            <Box
              key={i}
              sx={{ mb: 3, border: '1px solid #ddd', p: 2, borderRadius: 2 }}>
              <Grid container spacing={2} alignItems='center'>
                <Grid item xs={5}>
                  <TextField
                    select
                    fullWidth
                    label='Pay Mode'
                    size='small'
                    value={p.PayMode}
                    onChange={(e) =>
                      handlePaymentChange(i, 'PayMode', e.target.value)
                    }>
                    {['Cash', 'Card', 'UPI', 'Online', 'Wallet'].map((m) => (
                      <MenuItem key={m} value={m}>
                        {m}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={5}>
                  <TextField
                    fullWidth
                    label='Amount'
                    type='number'
                    size='small'
                    value={p.Amount}
                    onChange={(e) =>
                      handlePaymentChange(i, 'Amount', e.target.value)
                    }
                  />
                </Grid>
                <Grid item xs={2}>
                  <IconButton
                    onClick={() =>
                      i === payments.length - 1
                        ? handleAddPaymentRow()
                        : handleRemovePaymentRow(i)
                    }>
                    {i === payments.length - 1 ? <Add /> : <Remove />}
                  </IconButton>
                </Grid>
              </Grid>
              {p.PayMode === 'Card' && (
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      fullWidth
                      label='Card Type'
                      size='small'
                      value={p.CardType}
                      onChange={(e) =>
                        handlePaymentChange(i, 'CardType', e.target.value)
                      }>
                      <MenuItem value='Debit'>Debit</MenuItem>
                      <MenuItem value='Credit'>Credit</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label='Card Number (Last 4)'
                      size='small'
                      value={p.CardNumber}
                      onChange={(e) =>
                        handlePaymentChange(i, 'CardNumber', e.target.value)
                      }
                    />
                  </Grid>
                </Grid>
              )}
              {(p.PayMode === 'UPI' || p.PayMode === 'Online') && (
                <TextField
                  fullWidth
                  sx={{ mt: 2 }}
                  label='Transaction ID / Ref No'
                  size='small'
                  value={p.RefNo}
                  onChange={(e) =>
                    handlePaymentChange(i, 'RefNo', e.target.value)
                  }
                />
              )}
            </Box>
          ))}
        </DialogContent>
        <DialogActions sx={RESPONSIVE_DIALOG_ACTIONS_SX}>
          <Button onClick={() => setPaymentDialogOpen(false)}>Cancel</Button>
          <Button variant='contained' onClick={handleSavePayments}>
            Save Payment
          </Button>
        </DialogActions>
      </Dialog>

      {/* Update Details Dialog */}
      <Dialog
        open={updateDialogOpen}
        onClose={() => setUpdateDialogOpen(false)}
        maxWidth='md'
        fullWidth
        fullScreen={isMobile}>
        <DialogTitle sx={{ fontWeight: 'bold', textAlign: 'center' }}>
          Update Booking Details
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                size='small'
                label='Title'
                value={updateDetails.title || ''}
                onChange={(e) =>
                  setUpdateDetails({ ...updateDetails, title: e.target.value })
                }>
                <MenuItem value=''>— Select Title —</MenuItem>
                {TITLE_OPTIONS.map((t) => (
                  <MenuItem key={t} value={t}>
                    {t}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} />
            {/* Check-In / Check-Out edit panel — visibility & lock rules:
                • Not checked in yet → BOTH editable
                • Checked in (Ischeckin=1) → Check-In locked, Check-Out editable
                                              (so the front desk can extend stay)
                • Invoiced (Isinvoice=1) → BOTH locked (stay is closed) */}
            {(() => {
              const isCheckedIn = [true, 'true', 1].includes(
                currentUpdateBooking?.ischeckin,
              );
              const isInvoiced = [true, 'true', 1].includes(
                currentUpdateBooking?.isinvoice,
              );
              const checkInDisabled = isCheckedIn || isInvoiced;
              const checkOutDisabled = isInvoiced;
              return (
                <>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size='small'
                      type='datetime-local'
                      label='Check-In Date & Time'
                      InputLabelProps={{ shrink: true }}
                      value={updateDetails.checkIn || ''}
                      disabled={checkInDisabled}
                      onChange={(e) =>
                        setUpdateDetails({
                          ...updateDetails,
                          checkIn: e.target.value,
                        })
                      }
                      helperText={
                        isInvoiced
                          ? 'Locked — invoice generated'
                          : isCheckedIn
                            ? 'Locked — guest already checked in'
                            : ''
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size='small'
                      type='datetime-local'
                      label='Check-Out Date & Time'
                      InputLabelProps={{ shrink: true }}
                      value={updateDetails.checkOut || ''}
                      disabled={checkOutDisabled}
                      onChange={(e) =>
                        setUpdateDetails({
                          ...updateDetails,
                          checkOut: e.target.value,
                        })
                      }
                      inputProps={{
                        min: updateDetails.checkIn || undefined,
                      }}
                      helperText={
                        isInvoiced
                          ? 'Locked — invoice generated'
                          : updateDetails.checkIn && updateDetails.checkOut
                            ? `Stay duration: ${Math.max(
                                1,
                                Math.round(
                                  (new Date(updateDetails.checkOut) -
                                    new Date(updateDetails.checkIn)) /
                                    86400000,
                                ),
                              )} day(s)`
                            : 'Cannot select today or past dates'
                      }
                    />
                  </Grid>
                </>
              );
            })()}
            {[
              'firstName',
              'lastName',
              'email',
              'mobileNumber',
              'address',
              'city',
              'idType',
              'idNumber',
              'idName',
            ].map((field) => (
              <Grid item xs={12} sm={6} key={field}>
                <TextField
                  fullWidth
                  size='small'
                  label={field
                    .replace(/([A-Z])/g, ' $1')
                    .replace(/^./, (str) => str.toUpperCase())}
                  value={updateDetails[field] || ''}
                  onChange={(e) =>
                    setUpdateDetails({
                      ...updateDetails,
                      [field]: e.target.value,
                    })
                  }
                />
              </Grid>
            ))}

            {/* Headcount + per-guest editor. The OtherGuests array drives
                Bookingguestdetail rows on save; the primary guest stays in
                the standard updateDetails fields above. Empty rows are
                filtered out before sending. */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size='small'
                type='number'
                label='Adult Count'
                inputProps={{ min: 1 }}
                value={updateAdultCount}
                onChange={(e) => {
                  const n = Math.max(1, Number(e.target.value) || 1);
                  setUpdateAdultCount(n);
                  // Keep otherGuests length aligned with adultCount-1 so the
                  // UI rows correspond to actual co-guests. Grow by appending
                  // blanks, shrink by trimming the tail.
                  setUpdateOtherGuests((prev) => {
                    const needed = Math.max(0, n - 1);
                    if (needed === prev.length) return prev;
                    if (needed < prev.length) return prev.slice(0, needed);
                    const blanks = Array.from(
                      { length: needed - prev.length },
                      () => ({
                        title: '',
                        firstName: '',
                        lastName: '',
                        mobileNumber: '',
                      }),
                    );
                    return [...prev, ...blanks];
                  });
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size='small'
                type='number'
                label='Child Count'
                inputProps={{ min: 0 }}
                value={updateChildCount}
                onChange={(e) =>
                  setUpdateChildCount(Math.max(0, Number(e.target.value) || 0))
                }
              />
            </Grid>
            {updateOtherGuests.map((g, idx) => (
              <Grid item xs={12} key={`og-${idx}`}>
                <Box
                  sx={{
                    border: '1px solid #e3eaf3',
                    borderRadius: 2,
                    p: 1.5,
                    bgcolor: '#fafbff',
                  }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      mb: 1,
                    }}>
                    <Typography
                      sx={{ fontWeight: 600, fontSize: '0.82rem' }}>
                      Co-guest #{idx + 2}
                    </Typography>
                    <Button
                      size='small'
                      color='error'
                      onClick={() => {
                        setUpdateOtherGuests((prev) =>
                          prev.filter((_, i) => i !== idx),
                        );
                        setUpdateAdultCount((n) => Math.max(1, n - 1));
                      }}>
                      Remove
                    </Button>
                  </Box>
                  <Grid container spacing={1.5}>
                    <Grid item xs={6} sm={2}>
                      <TextField
                        select
                        fullWidth
                        size='small'
                        label='Title'
                        value={g.title || ''}
                        onChange={(e) =>
                          setUpdateOtherGuests((prev) => {
                            const next = [...prev];
                            next[idx] = { ...next[idx], title: e.target.value };
                            return next;
                          })
                        }>
                        <MenuItem value=''>—</MenuItem>
                        {TITLE_OPTIONS.map((t) => (
                          <MenuItem key={t} value={t}>
                            {t}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        size='small'
                        label='First Name'
                        value={g.firstName || ''}
                        onChange={(e) =>
                          setUpdateOtherGuests((prev) => {
                            const next = [...prev];
                            next[idx] = {
                              ...next[idx],
                              firstName: e.target.value,
                            };
                            return next;
                          })
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        fullWidth
                        size='small'
                        label='Last Name'
                        value={g.lastName || ''}
                        onChange={(e) =>
                          setUpdateOtherGuests((prev) => {
                            const next = [...prev];
                            next[idx] = {
                              ...next[idx],
                              lastName: e.target.value,
                            };
                            return next;
                          })
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        fullWidth
                        size='small'
                        label='Mobile'
                        value={g.mobileNumber || ''}
                        onChange={(e) =>
                          setUpdateOtherGuests((prev) => {
                            const next = [...prev];
                            next[idx] = {
                              ...next[idx],
                              mobileNumber: e.target.value,
                            };
                            return next;
                          })
                        }
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            ))}
            <Grid item xs={12}>
              <Button
                variant='outlined'
                size='small'
                onClick={() => {
                  setUpdateOtherGuests((prev) => [
                    ...prev,
                    {
                      title: '',
                      firstName: '',
                      lastName: '',
                      mobileNumber: '',
                    },
                  ]);
                  setUpdateAdultCount((n) => n + 1);
                }}>
                + Add Adult
              </Button>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <br />
            <b>Aadhaar Photo:</b>
            <Box
              sx={{
                mt: 1,
                display: 'flex',
                gap: 2,
                flexWrap: 'wrap',
              }}>
              {[
                {
                  label: 'Front',
                  file: aadharFront,
                  set: setAadharFront,
                  existing: currentUpdateBooking?.aadharFrontPath,
                },
                {
                  label: 'Back',
                  file: aadharBack,
                  set: setAadharBack,
                  existing: currentUpdateBooking?.aadharBackPath,
                },
              ].map((side) => (
                <Box
                  key={side.label}
                  sx={{
                    flex: '1 1 220px',
                    border: '1px solid #e3eaf3',
                    borderRadius: 2,
                    p: 2,
                    bgcolor: '#fafbff',
                  }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      mb: 1,
                    }}>
                    <Typography sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                      Aadhaar {side.label}
                    </Typography>
                    <Button
                      size='small'
                      variant='outlined'
                      component='label'
                      startIcon={<UploadIcon />}>
                      {side.file || side.existing ? 'Replace' : 'Upload'}
                      <input
                        type='file'
                        hidden
                        accept='image/*'
                        onChange={(e) => {
                          const f = e.target.files?.[0] || null;
                          side.set(f);
                          // Auto-fire OCR once both sides are chosen, mirroring
                          // ManageInventory's behaviour. Compute "other side"
                          // explicitly so the freshly-picked file is included.
                          const fr = side.label === 'Front' ? f : aadharFront;
                          const bk = side.label === 'Back' ? f : aadharBack;
                          if (fr && bk) runBookingAadharOCR(fr, bk);
                        }}
                      />
                    </Button>
                  </Box>
                  {side.file ? (
                    <Box sx={{ position: 'relative' }}>
                      <img
                        src={URL.createObjectURL(side.file)}
                        alt={`Aadhaar ${side.label}`}
                        style={{
                          width: '100%',
                          maxHeight: 140,
                          borderRadius: 6,
                          objectFit: 'cover',
                          border: '1px solid #ccc',
                        }}
                      />
                      <Typography
                        sx={{
                          fontSize: '0.7rem',
                          color: '#64748B',
                          mt: 0.5,
                        }}>
                        New file: {side.file.name}
                      </Typography>
                    </Box>
                  ) : side.existing ? (
                    <img
                      src={`${config.API_BASE_URL.replace(/\/api$/, '')}${side.existing}`}
                      alt={`Aadhaar ${side.label}`}
                      style={{
                        width: '100%',
                        maxHeight: 140,
                        borderRadius: 6,
                        objectFit: 'cover',
                        border: '1px solid #ccc',
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  ) : (
                    <Typography
                      sx={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                      No image uploaded yet.
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Paper
              elevation={3}
              sx={{
                p: 3,
                borderRadius: 3,
                bgcolor: '#f9fbfd',
                border: '1px solid #e3eaf3',
                mt: 2,
              }}>
              <Typography
                variant='h6'
                fontWeight='bold'
                sx={{
                  mb: 3,
                  color: '#1976d2',
                  borderBottom: '2px solid #1976d2',
                  display: 'inline-block',
                  pb: 0.5,
                }}>
                🏢 Company Details
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size='small'
                    label='Company Name'
                    value={updateDetails.companyname || ''}
                    onChange={(e) =>
                      setUpdateDetails({
                        ...updateDetails,
                        companyname: e.target.value,
                      })
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size='small'
                    label='GST No'
                    value={updateDetails?.gstno || ''}
                    onChange={(e) =>
                      setUpdateDetails({
                        ...updateDetails,
                        gstno: e.target.value,
                      })
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size='small'
                    label='Company Email'
                    value={updateDetails.companyEmail || ''}
                    onChange={(e) =>
                      setUpdateDetails({
                        ...updateDetails,
                        companyEmail: e.target.value,
                      })
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size='small'
                    label='Company Address'
                    multiline
                    rows={2}
                    value={updateDetails.companyAddress || ''}
                    onChange={(e) =>
                      setUpdateDetails({
                        ...updateDetails,
                        companyAddress: e.target.value,
                      })
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size='small'
                    label='City'
                    value={updateDetails.companyCity || ''}
                    onChange={(e) =>
                      setUpdateDetails({
                        ...updateDetails,
                        companyCity: e.target.value,
                      })
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size='small'
                    label='State'
                    value={updateDetails.companyState || ''}
                    onChange={(e) =>
                      setUpdateDetails({
                        ...updateDetails,
                        companyState: e.target.value,
                      })
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size='small'
                    label='Mobile'
                    value={updateDetails.companyMobile || ''}
                    onChange={(e) =>
                      setUpdateDetails({
                        ...updateDetails,
                        companyMobile: e.target.value,
                      })
                    }
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </DialogContent>
        <DialogActions
          sx={[
            { px: 3, py: 2, justifyContent: 'space-between' },
            RESPONSIVE_DIALOG_ACTIONS_SX,
          ]}>
          <Box display='flex' gap={2}>
            {/* Change Room / Change Package only make sense before invoice.
                Once Isinvoice=1 the stay is closed and modifying the
                billable structure would break the issued invoice. */}
            {![true, 'true', 1].includes(currentUpdateBooking?.isinvoice) && (
              <>
                <Can code={P.BOOKING_EDIT}>
                  <Button
                    variant='outlined'
                    color='primary'
                    component='a'
                    href='/admin/Changeroom'>
                    Change Room
                  </Button>
                </Can>
                <Can code={P.BOOKING_EDIT}>
                  <Button
                    variant='outlined'
                    color='secondary'
                    component='a'
                    href={`/admin/ChangePackage?packageId=${currentUpdateBooking?.packageId}&bookingId=${currentUpdateBooking?.bookingId}`}>
                    Change Package
                  </Button>
                </Can>
              </>
            )}
          </Box>
          <Box>
            <Button onClick={() => setUpdateDialogOpen(false)} color='inherit'>
              Cancel
            </Button>
            <Can code={P.BOOKING_EDIT}>
              <Button variant='contained' onClick={handleSaveUpdate}>
                Save Changes
              </Button>
            </Can>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Cancel Booking Dialog */}
      <Dialog
        open={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
        maxWidth='sm'
        fullWidth
        fullScreen={isMobile}>
        <DialogTitle sx={{ color: 'error.main' }}>Cancel Booking?</DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ mb: 2 }}>This action cannot be undone.</Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            sx={{ mb: 2 }}
            label='Cancellation Remark (Required)'
            value={cancelRemark}
            onChange={(e) => setCancelRemark(e.target.value)}
          />
          <TextField
            fullWidth
            type='number'
            sx={{ mb: 2 }}
            label='Refund Amount'
            value={Refundpayments.Amount}
            onChange={(e) =>
              setRefundPayments({ ...Refundpayments, Amount: e.target.value })
            }
          />
          <TextField
            select
            fullWidth
            sx={{ mb: 2 }}
            label='Pay Mode'
            value={Refundpayments.PayMode}
            onChange={(e) =>
              setRefundPayments({ ...Refundpayments, PayMode: e.target.value })
            }>
            {['Cash', 'UPI', 'Online'].map((m) => (
              <MenuItem key={m} value={m}>
                {m}
              </MenuItem>
            ))}
          </TextField>
          {(Refundpayments.PayMode === 'UPI' ||
            Refundpayments.PayMode === 'Online') && (
            <TextField
              fullWidth
              label='Transaction ID / Ref No'
              value={Refundpayments.RefNo}
              onChange={(e) =>
                setRefundPayments({ ...Refundpayments, RefNo: e.target.value })
              }
            />
          )}
        </DialogContent>
        <DialogActions sx={RESPONSIVE_DIALOG_ACTIONS_SX}>
          <Button onClick={() => setCancelDialogOpen(false)}>No</Button>
          <Button
            variant='contained'
            color='error'
            onClick={handleConfirmCancel}>
            Yes, Cancel Booking
          </Button>
        </DialogActions>
      </Dialog>

      {/* Late Checkout Dialog */}
      <Dialog
        open={lateCheckoutOpen}
        onClose={() => setLateCheckoutOpen(false)}
        maxWidth='sm'
        fullWidth
        fullScreen={isMobile}>
        <DialogTitle>Late Checkout Charges</DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ mb: 2 }}>
            Checkout time exceeded. Do you want to add extra charges?
          </Typography>
          <TextField
            fullWidth
            type='number'
            label='Extra Charge'
            sx={{ mb: 2 }}
            value={lateCheckoutData.amount}
            onChange={(e) =>
              setLateCheckoutData({
                ...lateCheckoutData,
                amount: e.target.value,
              })
            }
          />
          <TextField
            fullWidth
            multiline
            rows={3}
            label='Remark'
            value={lateCheckoutData.remark}
            onChange={(e) =>
              setLateCheckoutData({
                ...lateCheckoutData,
                remark: e.target.value,
              })
            }
          />
        </DialogContent>
        <DialogActions sx={RESPONSIVE_DIALOG_ACTIONS_SX}>
          <Button onClick={() => setLateCheckoutOpen(false)}>Cancel</Button>
          <Button
            variant='contained'
            onClick={() => {
              setLateCheckoutOpen(false);
              submitInvoice(
                lateCheckoutBooking,
                [],
                lateCheckoutData.amount,
                lateCheckoutData.remark,
              );
            }}>
            Generate Invoice
          </Button>
        </DialogActions>
      </Dialog>

      {/* Late Check-In Dialog */}
      <Dialog
        open={lateCheckInOpen}
        onClose={() => setLateCheckInOpen(false)}
        maxWidth='sm'
        fullWidth
        fullScreen={isMobile}>
        <DialogTitle>Late Check-In</DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            type='number'
            label='Early Check-In Charge'
            value={lateCheckInData.amount}
            onChange={(e) =>
              setLateCheckInData({ ...lateCheckInData, amount: e.target.value })
            }
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            multiline
            rows={3}
            label='Remark'
            value={lateCheckInData.remark}
            onChange={(e) =>
              setLateCheckInData({ ...lateCheckInData, remark: e.target.value })
            }
          />
        </DialogContent>
        <DialogActions sx={RESPONSIVE_DIALOG_ACTIONS_SX}>
          <Button onClick={() => setLateCheckInOpen(false)}>Cancel</Button>
          <Button
            variant='contained'
            onClick={() => {
              setLateCheckInOpen(false);
              doCheckInApi(lateBooking.bookingId, lateCheckInData);
            }}>
            Proceed Check-In
          </Button>
        </DialogActions>
      </Dialog>

      {/* Details Dialog */}
      <Dialog
        open={detailsOpen}
        onClose={handleCloseDetails}
        maxWidth='md'
        fullWidth
        fullScreen={isMobile}>
        <DialogTitle
          sx={{
            fontWeight: 'bold',
            bgcolor: '#1976d2',
            color: 'white',
            textAlign: 'center',
            fontSize: 20,
          }}>
          Booking Details
        </DialogTitle>
        <DialogContent sx={{ bgcolor: '#f4f6f8', p: 3 }}>
          {selectedBooking && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Paper sx={{ p: 2, borderRadius: 2 }}>
                  <Typography
                    variant='subtitle1'
                    fontWeight='bold'
                    mb={2}
                    color='primary'>
                    👤 Guest Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <b>Title:</b> {selectedBooking.title || '—'}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <b>First Name:</b> {selectedBooking.firstName}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <b>Last Name:</b> {selectedBooking.lastName}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <b>Email:</b> {selectedBooking.email}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <b>Mobile:</b> {selectedBooking.mobileNumber}
                    </Grid>
                    <Grid item xs={12}>
                      <b>Address:</b> {selectedBooking.address}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <b>City:</b> {selectedBooking.city}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <b>Nationality:</b> {selectedBooking.nationality}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <b>PassportNumber:</b> {selectedBooking.passportNumber}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <b>VisaNumber:</b> {selectedBooking.visaNumber}
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
              <Grid item xs={12}>
                <Paper sx={{ p: 2, borderRadius: 2 }}>
                  <Typography
                    variant='subtitle1'
                    fontWeight='bold'
                    mb={2}
                    color='primary'>
                    🪪 ID Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={4}>
                      <b>ID Type:</b> {selectedBooking.idType}
                    </Grid>
                    <Grid item xs={4}>
                      <b>ID No:</b> {selectedBooking.idNumber}
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
              <Grid item xs={12}>
                <Paper sx={{ p: 2, borderRadius: 2 }}>
                  <Typography
                    variant='subtitle1'
                    fontWeight='bold'
                    mb={2}
                    color='primary'>
                    🏨 Booking Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <b>Hotel:</b> {selectedBooking.hotelName}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <b>Room Type:</b> {selectedBooking.roomType}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <b>Remark:</b> {selectedBooking.bookingRemark}
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions
          sx={[{ bgcolor: '#f4f6f8', p: 2 }, RESPONSIVE_DIALOG_ACTIONS_SX]}>
          <Button
            variant='contained'
            color='primary'
            onClick={handleCloseDetails}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Payment Details Dialog */}
      <Dialog
        open={paymentDetailsOpen}
        onClose={() => setPaymentDetailsOpen(false)}
        maxWidth='md'
        fullWidth
        fullScreen={isMobile}>
        <DialogTitle
          sx={{ bgcolor: '#1976d2', color: 'white', fontWeight: 'bold' }}>
          Payment Details - Booking ID: {selectedBookingForPayment?.bookingId}
        </DialogTitle>
        <DialogContent dividers sx={{ bgcolor: '#f9f9f9' }}>
          {selectedBookingForPayment && (
            <Paper
              elevation={2}
              sx={{ p: 2, mb: 2, borderRadius: 2, background: '#fff3e0' }}>
              <Typography variant='h6' fontWeight='bold' color='secondary'>
                Customer Details
              </Typography>
              <Grid container spacing={2} mt={1}>
                <Grid item xs={4}>
                  <Typography>
                    <b>Name:</b> {selectedBookingForPayment.firstName}{' '}
                    {selectedBookingForPayment.lastName}
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography>
                    <b>Room No:</b> {roomLabel(selectedBookingForPayment.roomNumbers)}
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography>
                    <b>Mobile:</b> {selectedBookingForPayment.mobileNumber}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          )}
          {paymentDetailsLoading ? (
            <Box display='flex' justifyContent='center' my={4}>
              <CircularProgress />
            </Box>
          ) : selectedBookingPayments.length === 0 ? (
            <Typography textAlign='center' color='text.secondary' my={4}>
              No payments recorded for this booking.
            </Typography>
          ) : (
            <>
              {(() => {
                const p = selectedBookingPayments[0];
                const price = p?.price || 0;
                const days = p?.totalDays || 1;
                const base = price * days;
                const gstRate = base <= 7141.904761904762 ? 0.05 : 0.18;
                const gst = base * gstRate;
                const total = base + gst;
                return (
                  <Paper
                    elevation={2}
                    sx={{
                      p: 2,
                      mb: 2,
                      background: '#e3f2fd',
                      borderRadius: 2,
                    }}>
                    <Typography variant='h6' fontWeight='bold' color='primary'>
                      Package Summary
                    </Typography>
                    <Grid container spacing={2} mt={1}>
                      <Grid item xs={3}>
                        <Typography>
                          <b>Days:</b> {days}
                        </Typography>
                      </Grid>
                      <Grid item xs={3}>
                        <Typography>
                          <b>Price/Day:</b> {Companycurrency}
                          {price}
                        </Typography>
                      </Grid>
                      <Grid item xs={3}>
                        <Typography>
                          <b>Package Price:</b> {Companycurrency}
                          {base.toFixed(2)}
                        </Typography>
                      </Grid>
                      {isGstApplicable() && (
                        <Grid item xs={3}>
                          <Typography>
                            <b>GST ({gstRate * 100}%):</b> {Companycurrency}
                            {gst.toFixed(2)}
                          </Typography>
                        </Grid>
                      )}
                      <Grid item xs={12}>
                        <Typography
                          color='success.main'
                          fontWeight='bold'
                          textAlign='right'>
                          Total: {Companycurrency}
                          {total.toFixed(2)}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                );
              })()}
              <TableContainer component={Paper} elevation={3} sx={{ mt: 2 }}>
                <Table size='small'>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#e3f2fd' }}>
                      {[
                        'User',
                        'Days',
                        'Pay Mode',
                        'Amount',
                        'Type',
                        'Card Type',
                        'Card No',
                        'Ref/Txn ID',
                        'Date',
                        'Remark',
                      ].map((h) => (
                        <TableCell key={h}>
                          <b>{h}</b>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedBookingPayments.map((payment, idx) => (
                      <TableRow key={idx} hover>
                        <TableCell>{payment.username || '-'}</TableCell>
                        <TableCell>{payment.totalDays || '-'}</TableCell>
                        <TableCell>{payment.mode}</TableCell>
                        <TableCell align='right'>
                          {Companycurrency}
                          {payment.amount}
                        </TableCell>
                        <TableCell align='right'>
                          {payment.type || '-'}
                        </TableCell>
                        <TableCell>{payment.cardtype || '-'}</TableCell>
                        <TableCell>{payment.cardnumber || '-'}</TableCell>
                        <TableCell>{payment.refno || '-'}</TableCell>
                        <TableCell>
                          {payment.tranjectiondate
                            ? new Date(payment.tranjectiondate).toLocaleString(
                                'en-IN',
                              )
                            : '-'}
                        </TableCell>
                        <TableCell>{payment.remark || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
          {selectedBookingForPayment && (
            <Box mt={3} textAlign='right'>
              <Typography variant='h6' color='error' fontWeight='bold'>
                Pending Amount: {Companycurrency}
                {selectedBookingForPayment.pendingAmount || 0}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={RESPONSIVE_DIALOG_ACTIONS_SX}>
          {/* #5: undo the early check-in charge (removes it from pending). */}
          <Button
            variant='outlined'
            color='warning'
            onClick={() =>
              removeEarlyCheckinCharge(selectedBookingForPayment?.bookingId)
            }>
            Remove Early Check-in Charge
          </Button>
          <Button
            variant='contained'
            onClick={() => setPaymentDetailsOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Addon Confirm Dialog */}
      <Dialog
        open={addonConfirmOpen}
        onClose={() => {
          setAddonConfirmOpen(false);
          setBookingForInvoice(null);
        }}
        maxWidth='xs'
        fullWidth
        fullScreen={isMobile}>
        <DialogTitle>Generate Invoice</DialogTitle>
        <DialogContent dividers>
          <Typography variant='body1' sx={{ mb: 2 }}>
            Do you want to add any extra services/charges (Add-ons)?
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            For example: Extra Bed, Breakfast, Late Check-out charge, Pickup,
            etc.
          </Typography>
        </DialogContent>
        <DialogActions sx={RESPONSIVE_DIALOG_ACTIONS_SX}>
          <Button
            onClick={() => {
              setAddonConfirmOpen(false);
              submitInvoice(bookingForInvoice, []);
              setBookingForInvoice(null);
            }}>
            No, Generate Invoice Directly
          </Button>
          <Button
            variant='contained'
            color='primary'
            onClick={() => {
              setAddonConfirmOpen(false);
              setAddons([{ name: '', price: '' }]);
              setAddonInputOpen(true);
            }}>
            Yes, I want to add Add-ons
          </Button>
        </DialogActions>
      </Dialog>

      {/* Addon Input Dialog */}
      <Dialog
        open={addonInputOpen}
        onClose={() => setAddonInputOpen(false)}
        maxWidth='md'
        fullWidth
        fullScreen={isMobile}>
        <DialogTitle>Add-on Services / Extra Charges</DialogTitle>
        <DialogContent dividers>
          {addons.map((addon, index) => {
            const unitPrice = Number(addon.price) || 0;
            const quantity = Number(addon.qty) || 1;
            const totalForRow = unitPrice * quantity;
            return (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  gap: 2,
                  alignItems: 'center',
                  mb: 2,
                  p: 2,
                  border: '1px dashed #aaa',
                  borderRadius: 2,
                  bgcolor: '#f9f9f9',
                }}>
                <TextField
                  fullWidth
                  size='small'
                  label='Service / Item Name'
                  value={addon.name}
                  onChange={(e) =>
                    handleAddonChange(index, 'name', e.target.value)
                  }
                />
                <TextField
                  sx={{ width: 140 }}
                  size='small'
                  label={`Unit Price (${Companycurrency})`}
                  type='number'
                  value={addon.price}
                  onChange={(e) =>
                    handleAddonChange(index, 'price', e.target.value)
                  }
                  inputProps={{ min: 0 }}
                />
                <TextField
                  sx={{ width: 100 }}
                  size='small'
                  label='Qty'
                  type='number'
                  value={addon.qty}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (
                      val === '' ||
                      (Number(val) >= 1 && Number(val) <= 999)
                    ) {
                      handleAddonChange(index, 'qty', val);
                    }
                  }}
                  inputProps={{ min: 1, max: 999 }}
                />
                <Typography
                  sx={{
                    minWidth: 120,
                    textAlign: 'right',
                    fontWeight: 'bold',
                  }}>
                  {Companycurrency}
                  {totalForRow.toFixed(0)}
                </Typography>
                <IconButton
                  color='error'
                  size='small'
                  onClick={() => handleRemoveAddonRow(index)}
                  disabled={addons.length === 1}>
                  <Remove />
                </IconButton>
              </Box>
            );
          })}
          <Box
            sx={{
              mt: 2,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
            <Button
              startIcon={<Add />}
              variant='outlined'
              size='small'
              onClick={handleAddAddonRow}>
              Add Another Item
            </Button>
            {(() => {
              const gt = addons.reduce(
                (sum, a) => sum + Number(a.price || 0) * Number(a.qty || 1),
                0,
              );
              return (
                gt > 0 && (
                  <Typography variant='h6' color='primary' fontWeight='bold'>
                    Total Add-ons: {Companycurrency}
                    {gt.toFixed(0)}
                  </Typography>
                )
              );
            })()}
          </Box>
        </DialogContent>
        <DialogActions sx={RESPONSIVE_DIALOG_ACTIONS_SX}>
          <Button onClick={() => setAddonInputOpen(false)}>Cancel</Button>
          <Button
            variant='contained'
            color='success'
            onClick={async () => {
              const validAddons = addons
                .filter(
                  (a) =>
                    a.name?.trim() &&
                    Number(a.price) > 0 &&
                    Number(a.qty || 1) >= 1,
                )
                .map((a) => ({
                  name: a.name.trim(),
                  price: Number(a.price),
                  qty: Number(a.qty || 1),
                }));
              if (!validAddons.length) return;
              await saveBookingAddons(bookingForInvoice.bookingId, validAddons);
              setAddonInputOpen(false);
              setBookingForInvoice(null);
              setAddons([{ name: '', price: '', qty: '1' }]);
              window.location.reload();
            }}>
            Save Add-ons
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cover Charge Dialog */}
      <Dialog
        open={coverChargeOpen}
        onClose={() => setCoverChargeOpen(false)}
        maxWidth='sm'
        fullWidth
        fullScreen={isMobile}>
        <DialogTitle>Add Cover Charge</DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            type='number'
            label='Total Cover Charge (including GST)'
            value={coverTotalWithGST}
            onChange={(e) => setCoverTotalWithGST(e.target.value)}
            sx={{ mb: 3 }}
            InputProps={{
              startAdornment: (
                <Box component='span' sx={{ color: 'text.primary' }}>
                  {Companycurrency}
                </Box>
              ),
            }}
          />
          {coverTotalWithGST && (
            <Box sx={{ mb: 3, p: 2, bgcolor: '#f0f7fa', borderRadius: 2 }}>
              <Typography variant='body2' gutterBottom>
                Amount entered:{' '}
                <b>
                  {Companycurrency}
                  {Number(coverTotalWithGST).toFixed(2)}
                </b>
              </Typography>
              {(() => {
                const amt = Number(coverTotalWithGST);
                if (amt <= 0) return null;
                const gstRate = amt < 7875 ? 5 : 18;
                const gstAmount = amt * (gstRate / (100 + gstRate));
                const baseAmount = amt - gstAmount;
                return (
                  <>
                    <Typography color='success.main'>
                      GST Rate: <b>{gstRate}%</b>
                    </Typography>
                    <Typography>
                      Base Amount:{' '}
                      <b>
                        {Companycurrency}
                        {baseAmount.toFixed(2)}
                      </b>
                    </Typography>
                    <Typography color='primary' fontWeight='bold'>
                      GST Amount:{' '}
                      <b>
                        {Companycurrency}
                        {gstAmount.toFixed(2)}
                      </b>
                    </Typography>
                    <Typography
                      variant='body2'
                      color='text.secondary'
                      sx={{ mt: 1 }}>
                      (calculated as per current GST slab rules)
                    </Typography>
                  </>
                );
              })()}
            </Box>
          )}
          <TextField
            fullWidth
            multiline
            rows={2}
            label='Remark (optional)'
            value={coverRemark}
            onChange={(e) => setCoverRemark(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={RESPONSIVE_DIALOG_ACTIONS_SX}>
          <Button onClick={() => setCoverChargeOpen(false)}>Cancel</Button>
          <Button
            variant='contained'
            color='warning'
            disabled={!coverTotalWithGST || Number(coverTotalWithGST) <= 0}
            onClick={handleSaveCoverCharge}>
            Save Cover Charge
          </Button>
        </DialogActions>
      </Dialog>

      {/* Discount Modal */}
      <Dialog
        open={discountModalOpen}
        onClose={() => setDiscountModalOpen(false)}
        maxWidth='xs'
        fullWidth
        fullScreen={isMobile}>
        <DialogTitle sx={{ color: 'secondary.main', fontWeight: 'bold' }}>
          Apply Discount
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
            Booking ID: <strong>{currentDiscountBooking?.bookingId}</strong>
          </Typography>
          <TextField
            fullWidth
            type='number'
            label={`Unit Price (${Companycurrency})`}
            value={discountAmount}
            onChange={(e) => setDiscountAmount(e.target.value)}
            InputProps={{
              startAdornment: (
                <Box component='span' sx={{ color: 'text.primary' }}>
                  {Companycurrency}
                </Box>
              ),
            }}
            sx={{ mb: 3 }}
            autoFocus
          />
          <TextField
            fullWidth
            multiline
            rows={3}
            label='Remark (required)'
            value={discountRemark}
            onChange={(e) => setDiscountRemark(e.target.value)}
            placeholder='e.g. Corporate discount, festive offer, goodwill gesture...'
          />
        </DialogContent>
        <DialogActions sx={RESPONSIVE_DIALOG_ACTIONS_SX}>
          <Button onClick={() => setDiscountModalOpen(false)}>Cancel</Button>
          <Button
            variant='contained'
            color='secondary'
            onClick={handleSaveDiscount}
            disabled={actionLoading}>
            Apply Discount
          </Button>
        </DialogActions>
      </Dialog>

      {/* Manage Booking Dialog */}
      <Dialog
        open={manageDialogOpen}
        onClose={() => setManageDialogOpen(false)}
        maxWidth='sm'
        fullWidth
        fullScreen={isMobile}>
        <DialogTitle
          sx={{
            fontWeight: 'bold',
            textAlign: 'center',
            bgcolor: '#1565c0',
            color: '#fff',
          }}>
          Manage Booking
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            {/* Booking action timeline — newest at top. Renders the audit
                rows fetched from /Home/GetBookingActionHistory so the user
                can see check-in / discount / cover-charge / invoice events
                and their undos. */}
            <Grid item xs={12}>
              <Box
                sx={{
                  borderRadius: 2,
                  bgcolor: '#F8FAFC',
                  border: '1px solid #E2E8F0',
                  p: 1.5,
                  maxHeight: 220,
                  overflowY: 'auto',
                }}>
                <Typography
                  sx={{
                    fontSize: '0.78rem',
                    fontWeight: 700,
                    color: '#1565c0',
                    mb: 0.8,
                    letterSpacing: 0.4,
                  }}>
                  BOOKING TIMELINE
                </Typography>
                {timelineLoading ? (
                  <Typography sx={{ fontSize: '0.72rem', color: '#64748B' }}>
                    Loading…
                  </Typography>
                ) : bookingTimeline.length === 0 ? (
                  <Typography sx={{ fontSize: '0.72rem', color: '#64748B' }}>
                    No actions recorded yet.
                  </Typography>
                ) : (
                  bookingTimeline.map((row) => {
                    const isUndo = String(row.actionType || '').startsWith(
                      'Undo',
                    );
                    const icon = isUndo
                      ? '↺'
                      : row.actionType === 'CheckIn'
                        ? '✓'
                        : row.actionType === 'Discount'
                          ? '%'
                          : row.actionType === 'CoverCharge'
                            ? '＄'
                            : row.actionType === 'Invoice'
                              ? '🧾'
                              : '•';
                    const label = isUndo
                      ? `${row.actionType.replace('Undo', '')} reverted`
                      : `${row.actionType} added`;
                    const ts = row.createdDate
                      ? new Date(row.createdDate).toLocaleString('en-GB')
                      : '';
                    return (
                      <Box
                        key={row.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.8,
                          py: 0.3,
                          borderBottom: '1px dashed #E2E8F0',
                          '&:last-of-type': { borderBottom: 'none' },
                        }}>
                        <Box
                          sx={{
                            width: 22,
                            textAlign: 'center',
                            fontSize: '0.85rem',
                            color: isUndo ? '#ef6c00' : '#1565c0',
                            fontWeight: 700,
                          }}>
                          {icon}
                        </Box>
                        <Typography
                          sx={{
                            fontSize: '0.74rem',
                            fontWeight: 600,
                            color: row.isReverted ? '#94a3b8' : '#0f172a',
                            textDecoration: row.isReverted
                              ? 'line-through'
                              : 'none',
                            flex: 1,
                          }}>
                          {label}
                        </Typography>
                        <Typography
                          sx={{ fontSize: '0.68rem', color: '#64748B' }}>
                          {ts}
                        </Typography>
                      </Box>
                    );
                  })
                )}
              </Box>
            </Grid>

            {/* Rooms panel — remove a mistakenly-added room from this booking.
                Only when not yet invoiced, and only if more than one room. */}
            {!selectedManageBooking?.isinvoice &&
              String(selectedManageBooking?.roomNumbers || '')
                .split(',')
                .map((r) => r.trim())
                .filter(Boolean).length > 1 && (
                <Grid item xs={12}>
                  <Box
                    sx={{
                      borderRadius: 2,
                      bgcolor: '#F8FAFC',
                      border: '1px solid #E2E8F0',
                      p: 1.5,
                    }}>
                    <Typography
                      sx={{
                        fontSize: '0.78rem',
                        fontWeight: 700,
                        color: '#1565c0',
                        mb: 0.8,
                        letterSpacing: 0.4,
                      }}>
                      ROOMS
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {String(selectedManageBooking?.roomNumbers || '')
                        .split(',')
                        .map((r) => r.trim())
                        .filter(Boolean)
                        .map((rn) => (
                          <Box
                            key={rn}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.8,
                              border: '1px solid #CBD5E1',
                              borderRadius: 1.5,
                              px: 1,
                              py: 0.5,
                              bgcolor: '#fff',
                            }}>
                            <Typography
                              sx={{ fontSize: '0.8rem', fontWeight: 600 }}>
                              Room {rn}
                            </Typography>
                            <Button
                              size='small'
                              color='error'
                              variant='outlined'
                              sx={{
                                minWidth: 0,
                                px: 1,
                                py: 0,
                                fontSize: '0.68rem',
                                textTransform: 'none',
                              }}
                              onClick={() => handleRemoveRoomFromBooking(rn)}>
                              Remove
                            </Button>
                          </Box>
                        ))}
                    </Box>
                    <Typography
                      sx={{ fontSize: '0.66rem', color: '#94A3B8', mt: 0.8 }}>
                      Removing a room credits its charge back to the booking.
                    </Typography>
                  </Box>
                </Grid>
              )}

            {/* Add-ons panel.
                Shows every BookingAddon row attached to this booking.
                  - Active rows (EndDateTime == null) render a Remove button
                    so the guest's returned add-on stops accruing charge.
                  - Removed rows render struck-through with their EndDateTime
                    so audit history stays visible per spec. */}
            {!selectedManageBooking?.isinvoice && (
              <Grid item xs={12}>
                <Box
                  sx={{
                    borderRadius: 2,
                    bgcolor: '#F8FAFC',
                    border: '1px solid #E2E8F0',
                    p: 1.5,
                    maxHeight: 240,
                    overflowY: 'auto',
                  }}>
                  <Typography
                    sx={{
                      fontSize: '0.78rem',
                      fontWeight: 700,
                      color: '#1565c0',
                      mb: 0.8,
                      letterSpacing: 0.4,
                    }}>
                    ADD-ONS
                  </Typography>
                  {manageAddonsLoading ? (
                    <Typography sx={{ fontSize: '0.72rem', color: '#64748B' }}>
                      Loading…
                    </Typography>
                  ) : manageAddons.length === 0 ? (
                    <Typography sx={{ fontSize: '0.72rem', color: '#64748B' }}>
                      No add-ons on this booking.
                    </Typography>
                  ) : (
                    manageAddons.map((a) => {
                      const isRemoved = !!a.endDateTime;
                      const startStr = a.startDateTime
                        ? fmtDateTime(a.startDateTime)
                        : '—';
                      const endStr = a.endDateTime
                        ? fmtDateTime(a.endDateTime)
                        : null;
                      return (
                        <Box
                          key={a.id}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            py: 0.6,
                            borderBottom: '1px solid #E2E8F0',
                            '&:last-of-type': { borderBottom: 'none' },
                          }}>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              sx={{
                                fontSize: '0.78rem',
                                fontWeight: 600,
                                color: isRemoved ? '#94a3b8' : '#0f172a',
                                textDecoration: isRemoved
                                  ? 'line-through'
                                  : 'none',
                              }}>
                              {a.name}
                              {a.qty > 1 ? ` × ${a.qty}` : ''} —{' '}
                              {Companycurrency}
                              {Math.round(a.unitPrice * a.qty)}
                              /day
                            </Typography>
                            <Typography
                              sx={{ fontSize: '0.66rem', color: '#64748B' }}>
                              Started {startStr}
                              {endStr ? ` · Returned ${endStr}` : ''}
                            </Typography>
                          </Box>
                          {!isRemoved && (
                            <Can code={P.BOOKING_EDIT}>
                              <Button
                                size='small'
                                variant='outlined'
                                color='error'
                                sx={{ minWidth: 0, px: 1, fontSize: '0.7rem' }}
                                onClick={() => handleRemoveAddon(a.id)}>
                                Remove
                              </Button>
                            </Can>
                          )}
                          {isRemoved && (
                            <Chip
                              label='Removed'
                              size='small'
                              sx={{
                                height: 18,
                                fontSize: 9,
                                bgcolor: '#fff3e0',
                                color: '#ef6c00',
                              }}
                            />
                          )}
                        </Box>
                      );
                    })
                  )}
                </Box>
              </Grid>
            )}

            <Can code={P.BOOKING_EDIT}>
              <Grid item xs={12}>
                <Button
                  fullWidth
                  variant='contained'
                  color='primary'
                  onClick={() => handleOpenUpdate(selectedManageBooking)}>
                  Update
                </Button>
              </Grid>
            </Can>
            {Number(selectedManageBooking?.pendingAmount || 0) > 0 && (
              <Can code={P.BOOKING_ADD_PAYMENT}>
                <Grid item xs={12}>
                  <Button
                    fullWidth
                    variant='contained'
                    color='success'
                    onClick={() => handleAddPayment(selectedManageBooking)}>
                    + Payment
                  </Button>
                </Grid>
              </Can>
            )}
            <Can code={P.BOOKING_VIEW_PAYMENT}>
              <Grid item xs={12}>
                <Button
                  fullWidth
                  variant='outlined'
                  color='info'
                  onClick={() => handleViewPayments(selectedManageBooking)}>
                  View Payment
                </Button>
              </Grid>
            </Can>
            <Can code={P.BOOKING_VIEW}>
              <Grid item xs={12}>
                <Button
                  fullWidth
                  variant='outlined'
                  color='secondary'
                  onClick={() => {
                    handleOpenDetails(selectedManageBooking);
                    setManageDialogOpen(false);
                  }}>
                  View Details
                </Button>
              </Grid>
            </Can>
            <Can code={P.BOOKING_VIEW}>
              <Grid item xs={12}>
                <Button
                  fullWidth
                  variant='outlined'
                  color='info'
                  onClick={() => {
                    setManageDialogOpen(false);
                    openServicesView(selectedManageBooking, 'services');
                  }}>
                  Spa / Laundry / Mini-Bar
                </Button>
              </Grid>
            </Can>
            <Can code={P.BOOKING_VIEW}>
              <Grid item xs={12}>
                <Button
                  fullWidth
                  variant='outlined'
                  color='success'
                  onClick={() => {
                    setManageDialogOpen(false);
                    openServicesView(selectedManageBooking, 'restaurant');
                  }}>
                  Restaurant Orders
                </Button>
              </Grid>
            </Can>
            <Can code={P.BOOKING_VIEW}>
              <Grid item xs={12}>
                <Button
                  fullWidth
                  variant='outlined'
                  color='primary'
                  sx={{ borderStyle: 'dashed' }}
                  onClick={() => {
                    handleOpenConfirmation(selectedManageBooking);
                    setManageDialogOpen(false);
                  }}>
                  📄 Booking Confirmation
                </Button>
              </Grid>
            </Can>
            {!selectedManageBooking?.isinvoice && (
              <>
                {/* Undo buttons for discount / cover charge — only shown
                    when the corresponding action is already applied, and
                    only before invoice generation. */}
                {selectedManageBooking?.isCoverCharge && (
                  <Grid item xs={12}>
                    <Button
                      fullWidth
                      variant='outlined'
                      color='warning'
                      startIcon={<UndoIcon />}
                      onClick={() =>
                        handleUndoCoverCharge(selectedManageBooking?.bookingId)
                      }>
                      Undo Cover Charge
                    </Button>
                  </Grid>
                )}
                {selectedManageBooking?.isDiscount && (
                  <Grid item xs={12}>
                    <Button
                      fullWidth
                      variant='outlined'
                      color='secondary'
                      startIcon={<UndoIcon />}
                      onClick={() =>
                        handleUndoDiscount(selectedManageBooking?.bookingId)
                      }>
                      Undo Discount
                    </Button>
                  </Grid>
                )}
                {!selectedManageBooking?.isCoverCharge && (
                  <Can code={P.BOOKING_ADD_COVER_CHARGE}>
                    <Grid item xs={12}>
                      {/* Discount + cover charge are mutually exclusive.
                          Disable + tooltip rather than hide so the rule is
                          visible to the user. */}
                      <Tooltip
                        title={
                          selectedManageBooking?.isDiscount
                            ? 'Cover Charge cannot be added because Discount already exists.'
                            : ''
                        }>
                        <span>
                          <Button
                            fullWidth
                            variant='outlined'
                            color='warning'
                            disabled={!!selectedManageBooking?.isDiscount}
                            onClick={() =>
                              handleOpenCoverCharge(selectedManageBooking)
                            }>
                            Add Cover Charge
                          </Button>
                        </span>
                      </Tooltip>
                    </Grid>
                  </Can>
                )}
                {!selectedManageBooking?.isDiscount && (
                  <Can code={P.BOOKING_DISCOUNT}>
                    <Grid item xs={12}>
                      <Tooltip
                        title={
                          selectedManageBooking?.isCoverCharge
                            ? 'Discount cannot be added because Cover Charge already exists.'
                            : ''
                        }>
                        <span>
                          <Button
                            fullWidth
                            variant='outlined'
                            color='secondary'
                            disabled={!!selectedManageBooking?.isCoverCharge}
                            onClick={() =>
                              handleOpenDiscount(selectedManageBooking)
                            }>
                            Add Discount
                          </Button>
                        </span>
                      </Tooltip>
                    </Grid>
                  </Can>
                )}
                <Can code={P.BOOKING_MANAGE_ADDONS}>
                  <Grid item xs={12}>
                    <Button
                      fullWidth
                      variant='outlined'
                      color='inherit'
                      onClick={() => {
                        setBookingForInvoice(selectedManageBooking);
                        setAddons([{ name: '', price: '', qty: '1' }]);
                        setAddonInputOpen(true);
                      }}>
                      Add-ons
                    </Button>
                  </Grid>
                </Can>
                <Can code={P.BOOKING_GENERATE_INVOICE}>
                  <Grid item xs={12}>
                    <Button
                      fullWidth
                      variant='contained'
                      color='primary'
                      onClick={() =>
                        handleGenerateInvoice(selectedManageBooking)
                      }>
                      Generate Invoice
                    </Button>
                  </Grid>
                </Can>
                {!selectedManageBooking?.ischeckin && (
                  <Can code={P.BOOKING_CANCEL}>
                    <Grid item xs={12}>
                      <Button
                        fullWidth
                        variant='contained'
                        color='error'
                        onClick={() => handleOpenCancel(selectedManageBooking)}>
                        Cancel Booking
                      </Button>
                    </Grid>
                  </Can>
                )}
              </>
            )}
            {selectedManageBooking?.isinvoice && (
              <Can code={P.BOOKING_GENERATE_INVOICE}>
                <Grid item xs={12}>
                  <Button
                    variant='contained'
                    color='info'
                    fullWidth
                    onClick={() => handleViewInvoice(selectedManageBooking)}>
                    View Invoice
                  </Button>
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant='outlined'
                    color='warning'
                    fullWidth
                    startIcon={<UndoIcon />}
                    onClick={() =>
                      handleUndoInvoice(selectedManageBooking?.bookingId)
                    }>
                    Undo Invoice
                  </Button>
                </Grid>
              </Can>
            )}
            <Can code={P.BOOKING_PREVIEW_INVOICE}>
              <Grid item xs={12}>
                <Button
                  variant='contained'
                  color='secondary'
                  fullWidth
                  onClick={() => handlePreviewInvoice(selectedManageBooking)}>
                  Preview Invoice
                </Button>
              </Grid>
            </Can>
          </Grid>
        </DialogContent>
        <DialogActions sx={[{ px: 3, py: 2 }, RESPONSIVE_DIALOG_ACTIONS_SX]}>
          <Button
            fullWidth
            variant='outlined'
            onClick={() => setManageDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Invoice Preview Dialog */}
      <Dialog
        open={invoicePreviewOpen}
        onClose={() => setInvoicePreviewOpen(false)}
        maxWidth='lg'
        fullWidth
        fullScreen={isMobile}>
        <DialogContent
          sx={{
            bgcolor: '#dfe3e6',
            p: { xs: 1, sm: 4 },
            overflowX: 'auto',
          }}>
          {selectedManageBooking && (
            <Box
              id='printableInvoice'
              sx={{
                maxWidth: 950,
                mx: 'auto',
                bgcolor: '#fff',
                border: '2px solid #000',
                p: { xs: 1.5, sm: 4 },
                fontFamily: 'Arial',
                color: '#000',
                fontSize: { xs: 12, sm: 14 },
              }}>
              <Box sx={{ borderBottom: '2px solid #000', pb: 2, mb: 3 }}>
                <Grid container alignItems='center'>
                  <Grid item xs={12}>
                    Invoice Preview
                  </Grid>
                </Grid>
              </Box>
              {/* BILL TO / BILL FROM — stack on mobile, side-by-side on
                  desktop. flexWrap='nowrap' previously squeezed both into
                  one row on phones; using default wrap now. */}
              <Grid container spacing={2} mb={3}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ border: '1px solid #000', p: 2, minHeight: 210 }}>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        borderBottom: '1px solid #000',
                        pb: 1,
                        mb: 2,
                        fontSize: 18,
                      }}>
                      BILL TO
                    </Typography>
                    <Typography sx={{ mb: 1 }}>
                      <b>Name :</b>{' '}
                      {selectedManageBooking.title
                        ? `${selectedManageBooking.title} `
                        : ''}
                      {selectedManageBooking.firstName}{' '}
                      {selectedManageBooking.lastName}
                    </Typography>
                    <Typography sx={{ mb: 1 }}>
                      <b>Mobile :</b> {selectedManageBooking.mobileNumber}
                    </Typography>
                    <Typography sx={{ mb: 1 }}>
                      <b>Email :</b> {selectedManageBooking.email}
                    </Typography>
                    <Typography sx={{ mb: 1 }}>
                      <b>Address :</b> {selectedManageBooking.address}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ border: '1px solid #000', p: 2, minHeight: 210 }}>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        borderBottom: '1px solid #000',
                        pb: 1,
                        mb: 2,
                        fontSize: 18,
                      }}>
                      BOOKING DETAILS
                    </Typography>
                    <Typography sx={{ mb: 1 }}>
                      <b>Room No :</b> {roomLabel(selectedManageBooking.roomNumbers)}
                    </Typography>
                    <Typography sx={{ mb: 1 }}>
                      <b>Room Type :</b> {selectedManageBooking.roomType}
                    </Typography>
                    <Typography sx={{ mb: 1 }}>
                      <b>Check-In :</b>{' '}
                      {fmtDateTime(selectedManageBooking.checkIn)}
                    </Typography>
                    <Typography sx={{ mb: 1 }}>
                      <b>Check-Out :</b>{' '}
                      {fmtDateTime(selectedManageBooking.checkOut)}
                    </Typography>
                    <Typography sx={{ mb: 1 }}>
                      <b>Guests :</b> {selectedManageBooking.adultCount} Adult
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
              <Table
                sx={{
                  border: '1px solid #000',
                  '& td, & th': { border: '1px solid #000' },
                }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#efefef' }}>
                    <TableCell>
                      <Typography fontWeight='bold'>Description</Typography>
                    </TableCell>
                    <TableCell align='right'>
                      <Typography fontWeight='bold'>Amount</Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>Room Base Price</TableCell>
                    <TableCell align='right'>
                      {Companycurrency} {roomBasePrice.toFixed(2)}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Cover Base Price</TableCell>
                    <TableCell align='right'>
                      {Companycurrency} {coverBaseAmount.toFixed(2)}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <b>Total Base Amount</b>
                    </TableCell>
                    <TableCell align='right'>
                      <b>{Companycurrency} {totalBaseAmount.toFixed(2)}</b>
                    </TableCell>
                  </TableRow>
                  {/* GST lines only for GST companies (India). VAT / No-Tax
                      companies must not see GST rows on the invoice. */}
                  {isGstApplicable() && (
                    <>
                      <TableRow>
                        <TableCell>GST ({roomGstPercent}%)</TableCell>
                        <TableCell align='right'>{Companycurrency} {totalGST.toFixed(2)}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Total With GST</TableCell>
                        <TableCell align='right'>
                          {Companycurrency} {totalWithGST.toFixed(2)}
                        </TableCell>
                      </TableRow>
                    </>
                  )}
                  <TableRow>
                    <TableCell>
                      <b>Add-on Charges</b>
                    </TableCell>
                    <TableCell align='right'>
                      <b>{Companycurrency} {addonAmount.toFixed(2)}</b>
                    </TableCell>
                  </TableRow>
                  {/* Itemised addons (Name + Price) fetched via
                      /Home/Getinvoicedata. Skip cancellation/refund rows
                      with fleg === 'R' so they don't clutter the bill. */}
                  {previewAddons
                    .filter((a) => String(a.fleg).toUpperCase() !== 'R')
                    .map((a, i) => (
                      <TableRow key={`addon-${i}`}>
                        <TableCell sx={{ pl: 4, fontStyle: 'italic' }}>
                          • {a.name}
                        </TableCell>
                        <TableCell align='right' sx={{ fontStyle: 'italic' }}>
                          {Companycurrency} {Number(a.price).toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ))}
                  {previewAddons.length === 0 && addonAmount > 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={2}
                        sx={{
                          fontStyle: 'italic',
                          color: 'text.secondary',
                          fontSize: 12,
                        }}>
                        (Add-on details unavailable — total shown above.)
                      </TableCell>
                    </TableRow>
                  )}
                  <TableRow>
                    <TableCell>Discount</TableCell>
                    <TableCell align='right'>
                      {invoiceDiscountAmount.toFixed(2)}
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell>
                      <Typography sx={{ fontWeight: 700, fontSize: 20 }}>
                        GRAND TOTAL
                      </Typography>
                    </TableCell>
                    <TableCell align='right'>
                      {/* Use `grandTotal` (room+GST + addons + cover − discount),
                          not the bare `totalWithGST` which excludes addons. */}
                      <Typography sx={{ fontWeight: 700, fontSize: 20 }}>
                        {Companycurrency} {grandTotal.toFixed(2)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
              <Grid container mt={6} spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography sx={{ fontWeight: 700, mb: 1 }}>
                    Terms & Conditions
                  </Typography>
                  <Typography sx={{ mb: 1 }}>
                    • Goods once sold will not be taken back.
                  </Typography>
                  <Typography sx={{ mb: 1 }}>
                    • Thank you for staying with us.
                  </Typography>
                  <Typography sx={{ mb: 1 }}>• Visit Again 🙏</Typography>
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={6}
                  textAlign={{ xs: 'left', sm: 'right' }}>
                  <Typography sx={{ fontWeight: 700, mb: 8 }}>
                    For {company?.companyName}
                  </Typography>
                  <Typography
                    sx={{
                      borderTop: '1px solid #000',
                      display: 'inline-block',
                      pt: 1,
                      minWidth: 180,
                    }}>
                    Authorized Signatory
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions
          sx={[{ p: 2, bgcolor: '#fff' }, RESPONSIVE_DIALOG_ACTIONS_SX]}>
          <Button
            variant='outlined'
            onClick={() => setInvoicePreviewOpen(false)}>
            Close
          </Button>
          <Button
            variant='contained'
            onClick={() => {
              const printContents =
                document.getElementById('printableInvoice').innerHTML;
              const win = window.open('', '', 'width=1200,height=900');
              win.document.write(
                `<html><head><title>Invoice</title><style>body{margin:0;padding:20px;font-family:Arial,sans-serif;background:#f2f2f2;}#wrapper{max-width:950px;margin:auto;background:#fff;border:2px solid #000;padding:30px;}table{width:100%;border-collapse:collapse;margin-top:20px;}table th,table td{border:1px solid #000;padding:10px;font-size:14px;}table th{background:#efefef;}@media print{body{background:#fff;}}</style></head><body><div id="wrapper">${printContents}</div></body></html>`,
              );
              win.document.close();
              setTimeout(() => {
                win.focus();
                win.print();
              }, 500);
            }}>
            Print Invoice
          </Button>
        </DialogActions>
      </Dialog>

      <Backdrop
        open={actionLoading}
        sx={{
          color: '#fff',
          zIndex: (theme) => theme.zIndex.drawer + 999,
          backdropFilter: 'blur(2px)',
        }}>
        <Box textAlign='center'>
          <CircularProgress color='inherit' size={55} />
          <Typography mt={2} fontWeight='bold'>
            {actionText || 'Please wait...'}
          </Typography>
        </Box>
      </Backdrop>

      <Backdrop
        open={ocrLoading}
        sx={{
          color: '#fff',
          zIndex: (theme) => theme.zIndex.drawer + 1000,
          backdropFilter: 'blur(2px)',
        }}>
        <Box textAlign='center'>
          <CircularProgress color='inherit' size={55} />
          <Typography mt={2} fontWeight='bold'>
            Scanning Aadhaar...
          </Typography>
        </Box>
      </Backdrop>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity} variant='filled'>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
